import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  Link as MuiLink,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material'
import AccountBalance from '@mui/icons-material/AccountBalance'
import AlternateEmail from '@mui/icons-material/AlternateEmail'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import Article from '@mui/icons-material/Article'
import AutoAwesome from '@mui/icons-material/AutoAwesome'
import Badge from '@mui/icons-material/Badge'
import Description from '@mui/icons-material/Description'
import Draw from '@mui/icons-material/Draw'
import Language from '@mui/icons-material/Language'
import LocalHospital from '@mui/icons-material/LocalHospital'
import LinkIcon from '@mui/icons-material/Link'
import Payments from '@mui/icons-material/Payments'
import PersonPinCircle from '@mui/icons-material/PersonPinCircle'
import Psychology from '@mui/icons-material/Psychology'
import School from '@mui/icons-material/School'
import ArrowBack from '@mui/icons-material/ArrowBack'
import CheckCircle from '@mui/icons-material/CheckCircle'
import HighlightOff from '@mui/icons-material/HighlightOff'
import HelpOutline from '@mui/icons-material/HelpOutline'
import Close from '@mui/icons-material/Close'
import OpenInNew from '@mui/icons-material/OpenInNew'
import VerifiedUser from '@mui/icons-material/VerifiedUser'
import Work from '@mui/icons-material/Work'
import WorkspacePremium from '@mui/icons-material/WorkspacePremium'
import { Route, Routes, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { toast } from 'react-toastify'
import axios from 'axios'
import { apiURL, mediaURL } from 'src/consumer/services'
import { executeRecaptchaV3 } from 'src/views/applyPublic/recaptchaV3'
import PictureUploadComponent from 'src/consumer/components/Helpers/PictureUploadComponent/PictureUploadComponent'
import SignaturePad from 'src/consumer/components/Helpers/SignaturePad/SignaturePad'
import LocationService from 'src/services/APIs/Geolocation/GetGeolocationScript'

// Phase 14z — canonical brand URLs for the agreement checkboxes; pulled from
// src/consumer/constants.js so the apply page stays in lockstep with the rest of the brand.
const APPLY_TERMS_URL = 'https://aumhum.com/terms'
const APPLY_PRIVACY_POLICY_URL = 'https://aumhum.com/privacy-policy-1'

// Phase 14z — well-known agreement ids the apply form uses for the mandatory ToS / Privacy
// tickboxes. The support team can later wire these to the formal UserAgreement records by
// matching on the same id; for now they're just stable strings the audit log can reference.
const TERMS_AGREEMENT_ID = 'apply-tos-2025'
const PRIVACY_AGREEMENT_ID = 'apply-privacy-2025'

/**
 * Phase 14z — fetch IP + approximate geolocation at consent time so the support / legal team
 * has an audit trail. Best-effort: each piece of context is independently optional, and the
 * caller must never block UX on these calls. Returns { ip, latLng, userAgent }.
 */
async function captureAcceptanceAuditContext() {
  const out = { ip: '', latLng: '', userAgent: '' }
  if (typeof navigator !== 'undefined' && navigator.userAgent) {
    out.userAgent = navigator.userAgent
  }
  // IP via ipapi (same source the existing legal-form flow uses).
  try {
    const res = await fetch('https://ipapi.co/json/')
    const data = await res.json()
    if (data?.ip) out.ip = data.ip
  } catch {
    /* non-fatal */
  }
  // Approx geolocation via Google Geolocation (no perms prompt — server-side IP-based).
  try {
    const loc = await LocationService.getLocation()
    if (loc?.lat != null && loc?.lng != null) {
      out.latLng = `${loc.lat},${loc.lng}`
    }
  } catch {
    /* non-fatal */
  }
  return out
}

// Hit the API server directly (manage.aumhum.com) — NOT publicApiURL. publicApiURL points at
// papi.aumhum.com which is reserved for the cacheable /api/public/** surface; our /api/nc/**
// invitation + apply + OTP endpoints are explicitly non-cacheable, so they belong on the
// regular API host alongside /api/secure/**. Sending /api/nc traffic to papi would either
// short-circuit through its CDN (defeating the "nc" guarantee) or 404 if the route isn't
// proxied there at all.
const API_BASE = apiURL || ''
// /api/nc/ ("not cached") bypasses the CDN that fronts our domain — /api/public/ was returning
// stale GET responses (notably an "invitation not found" snapshot for newly-minted tokens).
const PUBLIC_BASE = '/api/nc/practitioner-applications'

// Phase 14aj — dedicated axios instance for the PUBLIC apply / invitation / OTP endpoints.
// The applicant might be signed in to AUMHUM as a different account at the moment they fill
// /apply; if their existing JWT (or any other ambient auth) leaked into these calls, downstream
// services on the API would attribute the new applicant's actions to the wrong user (silent
// role assignments, tainted audit trails). The interceptor below scrubs the Authorization
// header off every outbound request from this instance regardless of what default / per-call
// config does, guaranteeing the apply flow is anonymous on the wire. The backend's
// PublicApplyAnonymizationFilter is the matching server-side guard.
//
// withCredentials stays false (axios's default) so cookies don't piggy-back cross-origin either.
const applyAnonAxios = axios.create({ baseURL: API_BASE, withCredentials: false })
applyAnonAxios.interceptors.request.use((config) => {
  if (config.headers) {
    // Remove any Authorization that might have been merged in from defaults / per-call opts.
    delete config.headers.Authorization
    delete config.headers.authorization
  }
  return config
})

// Helper that runs a v3 captcha challenge and returns the token. The backend uses the
// platform-wide RecaptchaService (recaptcha.* properties + per-host credentials), and that
// service reads the token from the request body field `recaptchaToken` for POSTs or the
// `recaptchaToken` query param for GETs. Returns null when the site key isn't configured
// (dev) so the backend's recaptcha.enabled=false flag accepts the missing token.
async function fetchRecaptchaToken(action) {
  const token = await executeRecaptchaV3(action)
  return token || null
}

// Public-facing labels never use the internal "therapist" word.
const TYPE_TILES = [
  {
    id: 'doctor',
    label: 'Doctor',
    description: 'Apply to join AUMHUM as a doctor — any specialty.',
    Icon: LocalHospital,
    gradient: 'linear-gradient(135deg, rgba(0, 43, 107, 0.95) 0%, rgba(30, 58, 138, 0.95) 100%)',
  },
  {
    id: 'mhp',
    label: 'Mental Health Professional',
    description: 'Psychologist, psychotherapist, counsellor, or nutritionist.',
    Icon: Psychology,
    gradient: 'linear-gradient(135deg, rgba(216, 101, 0, 0.95) 0%, rgba(194, 65, 12, 0.95) 100%)',
  },
  {
    id: 'psychology-student',
    label: 'Psychology Student Program',
    description: 'Currently enrolled psychology students — apply for the program.',
    Icon: School,
    gradient: 'linear-gradient(135deg, rgba(99, 102, 241, 0.95) 0%, rgba(67, 56, 202, 0.95) 100%)',
  },
]

const MHP_ROLE_TILES = [
  { id: 'psychologist', label: 'Psychologist', discipline: 'psychotherapy' },
  { id: 'psychotherapist', label: 'Psychotherapist', discipline: 'psychotherapy' },
  { id: 'counsellor', label: 'Counsellor', discipline: 'psychotherapy' },
  { id: 'nutritionist', label: 'Nutritionist', discipline: 'nutrition-consultation' },
]

const SUBCATEGORY_OPTIONS = {
  psychologist: ['Clinical', 'Counselling', 'Educational', 'Sport', 'Forensic'],
  psychotherapist: ['Clinical', 'Counselling', 'Family', 'Group'],
  counsellor: ['Couples', 'Family', 'Career', 'Addictions'],
  nutritionist: ['Clinical', 'Sport', 'Pediatric', 'Geriatric'],
}

// Phase 14u — single canonical logo URL used across the public /apply surfaces. Pinned to the
// prod CDN copy that the email templates also use, so applicants land on the same brand mark.
const APPLY_LOGO_URL =
  'https://media.aumhum.com/images/prod/MEDIA-e8bd40c2af6e4d49b4d2aef2613c3234'
const PUBLIC_PROFILE_URL_PREFIX = 'https://aumhum.com/mentor/'
const LINKEDIN_PROFILE_URL_PREFIX = 'https://www.linkedin.com/in/'
const SECTION_ACCENTS = {
  identity: { color: '#1e3151', tint: 'rgba(30,49,81,0.06)' },
  contact: { color: '#0f766e', tint: 'rgba(15,118,110,0.07)' },
  bio: { color: '#7c3aed', tint: 'rgba(124,58,237,0.06)' },
  links: { color: '#c2410c', tint: 'rgba(194,65,12,0.06)' },
  credentials: { color: '#2563eb', tint: 'rgba(37,99,235,0.06)' },
  location: { color: '#047857', tint: 'rgba(4,120,87,0.06)' },
  money: { color: '#b45309', tint: 'rgba(180,83,9,0.07)' },
  consent: { color: '#7c3aed', tint: 'rgba(124,58,237,0.06)' },
}

function titleize(value) {
  return String(value || '')
    .replace(/[-_]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (ch) => ch.toUpperCase())
}

function withArticle(label) {
  const clean = String(label || '').trim()
  if (!clean) return ''
  return `${/^[aeiou]/i.test(clean) ? 'an' : 'a'} ${clean}`
}

function getTypeLabel(type) {
  return TYPE_TILES.find((tile) => tile.id === type)?.label || titleize(type || 'practitioner')
}

function getRoleLabel(role, practitionerType) {
  if (role) return titleize(role)
  if (practitionerType === 'doctor') return 'Doctor'
  if (practitionerType === 'screener') return 'Screener'
  if (practitionerType === 'psychology-student') return 'Psychology Student'
  if (practitionerType === 'therapist') return 'Mental Health Professional'
  return 'Practitioner'
}

function getDisciplineLabel(discipline, practitionerType) {
  if (discipline === 'psychotherapy') return 'Psychotherapy'
  if (discipline === 'nutrition-consultation') return 'Nutrition Consultation'
  if (discipline) return titleize(discipline)
  if (practitionerType === 'doctor') return 'Medical Care'
  if (practitionerType === 'screener') return 'Clinical Screening'
  return 'Health and Wellbeing'
}

function stripQueryAndHash(value) {
  return String(value || '').split(/[?#]/)[0]
}

function sanitizeProfileSlug(input) {
  const raw = stripQueryAndHash(input)
    .trim()
    .replace(/^https?:\/\/(www\.)?aumhum\.com\/mentor\//i, '')
    .replace(/^\/?mentor\//i, '')
    .split('/')[0]
  return raw
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function sanitizeLinkedinSlug(input) {
  const raw = stripQueryAndHash(input)
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/^(www\.)?(in\.)?linkedin\.com\//i, '')
    .replace(/^\/+/g, '')
    .replace(/^in\//i, '')
    .split('/')[0]
  return raw.replace(/[^a-zA-Z0-9-]/g, '')
}

function suggestedSlugFromName(name, fallback) {
  return sanitizeProfileSlug(name || fallback || 'your-name')
}

function maskEmail(value) {
  const email = String(value || '').trim()
  const [local, domain] = email.split('@')
  if (!local || !domain) return ''
  const visibleLocal =
    local.length <= 2
      ? `${local.charAt(0)}***`
      : `${local.charAt(0)}***${local.charAt(local.length - 1)}`
  return `${visibleLocal}@${domain}`
}

function maskMobile(value) {
  const digits = String(value || '').replace(/\D/g, '')
  if (!digits) return ''
  const last = digits.slice(-4)
  return `******${last}`
}

function normalizeResumeHint(rawHint, invitation) {
  const source = rawHint || {}
  let channel = source.channel === 'mobile' || source.channel === 'whatsapp'
    ? 'mobile'
    : source.channel === 'email'
      ? 'email'
      : ''
  let maskedContact = source.maskedContact || source.masked || ''

  if (!channel) {
    if (invitation?.prefillEmail) channel = 'email'
    else if (invitation?.prefillMobile) channel = 'mobile'
  }
  if (!maskedContact) {
    maskedContact = channel === 'email'
      ? maskEmail(invitation?.prefillEmail)
      : maskMobile(invitation?.prefillMobile)
  }

  return {
    channel,
    channelLabel: channel === 'mobile' ? 'WhatsApp number' : 'Email',
    maskedContact,
  }
}

function buildResumeUrl(resumeHint) {
  const params = new URLSearchParams()
  params.set('source', 'invitation')
  if (resumeHint?.channel) params.set('channel', resumeHint.channel)
  if (resumeHint?.maskedContact) params.set('hint', resumeHint.maskedContact)
  return `/apply/resume?${params.toString()}`
}

function buildBioSuggestions({ practitionerType, discipline, role, category } = {}) {
  const roleLabel = getRoleLabel(role, practitionerType)
  const disciplineLabel = getDisciplineLabel(discipline, practitionerType)
  const categoryLabel = category ? titleize(category) : ''

  if (discipline === 'nutrition-consultation' || role === 'nutritionist') {
    return [
      `I am ${withArticle(roleLabel.toLowerCase())} focused on sustainable nutrition plans, metabolic health, and habit-building that fits a client's daily life.`,
      `My work combines clinical nutrition, realistic meal planning, and gentle accountability so clients can make changes that last.`,
      categoryLabel
        ? `I bring experience in ${categoryLabel.toLowerCase()} nutrition and tailor guidance to each client's medical history, preferences, and routine.`
        : `I support clients with practical, evidence-informed nutrition guidance across lifestyle, preventive, and condition-specific goals.`,
    ]
  }

  if (practitionerType === 'doctor') {
    return [
      `I am ${withArticle(roleLabel.toLowerCase())} focused on thoughtful assessment, clear treatment planning, and collaborative follow-up for people seeking mental health and wellbeing support.`,
      `My practice combines medical rigour with practical, patient-centred guidance so clients understand their options and next steps.`,
      categoryLabel
        ? `I have experience in ${categoryLabel.toLowerCase()} care and work closely with clients to make treatment plans understandable and actionable.`
        : `I value careful listening, transparent clinical reasoning, and coordinated care with the wider support team.`,
    ]
  }

  if (practitionerType === 'screener') {
    return [
      'I conduct structured mental health screenings to help clients and clinical teams identify needs early and choose the right level of care.',
      'My work focuses on clear intake conversations, risk-sensitive documentation, and warm handoffs into the right clinical pathway.',
      categoryLabel
        ? `I support ${categoryLabel.toLowerCase()} screenings with a balance of empathy, structure, and practical next-step guidance.`
        : 'I help make the first clinical touchpoint calm, organised, and useful for both clients and practitioners.',
    ]
  }

  return [
    `I am ${withArticle(roleLabel.toLowerCase())} trained in ${disciplineLabel.toLowerCase()}, working with clients on anxiety, mood, relationships, stress, and life transitions.`,
    'My approach is collaborative and evidence-informed, with a focus on psychological safety, clear goals, and skills clients can use between sessions.',
    categoryLabel
      ? `I have a particular interest in ${categoryLabel.toLowerCase()} work and adapt sessions to each client's pace, context, and support needs.`
      : 'I work with clients to build insight, emotional regulation, and sustainable coping strategies over time.',
  ]
}

function FieldSection({ title, caption, icon: Icon, accent = 'identity', children, action }) {
  const tone = SECTION_ACCENTS[accent] || SECTION_ACCENTS.identity
  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2 },
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        background: `linear-gradient(135deg, ${tone.tint} 0%, rgba(255,255,255,0.96) 42%, #ffffff 100%)`,
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          {Icon && (
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                display: 'grid',
                placeItems: 'center',
                color: tone.color,
                backgroundColor: 'rgba(255,255,255,0.78)',
                border: '1px solid rgba(15,23,42,0.08)',
              }}
            >
              <Icon fontSize="small" />
            </Box>
          )}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 750, lineHeight: 1.25 }}>
              {title}
            </Typography>
            {caption && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {caption}
              </Typography>
            )}
          </Box>
        </Stack>
        {action}
      </Stack>
      {children}
    </Box>
  )
}

function UrlSlugField({
  label,
  value,
  onChange,
  prefix,
  placeholder,
  helperText,
  normalizeSlug = sanitizeProfileSlug,
  storeAsUrl = false,
  trailingSlash = false,
  required = false,
}) {
  const slug = normalizeSlug(value)
  const preview = slug ? `${prefix}${slug}${trailingSlash ? '/' : ''}` : ''
  return (
    <Box>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {label}
          {required && (
            <Box component="span" sx={{ color: 'error.main', ml: 0.25 }}>
              *
            </Box>
          )}
        </Typography>
        <LinkIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
      </Stack>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'max-content minmax(160px, 1fr)' },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          overflow: 'hidden',
          backgroundColor: '#fff',
          '&:focus-within': {
            borderColor: 'primary.main',
            boxShadow: '0 0 0 3px rgba(124,58,237,0.12)',
          },
        }}
      >
        <Box
          sx={{
            px: 1.25,
            py: 1.15,
            color: 'text.secondary',
            fontFamily: 'monospace',
            fontSize: 13,
            lineHeight: 1.4,
            backgroundColor: 'rgba(15,23,42,0.04)',
            borderRight: { xs: 0, sm: '1px solid' },
            borderBottom: { xs: '1px solid', sm: 0 },
            borderColor: 'divider',
            minWidth: 0,
            overflowWrap: 'anywhere',
          }}
        >
          {prefix}
        </Box>
        <TextField
          value={slug}
          onChange={(event) => {
            const nextSlug = normalizeSlug(event.target.value)
            onChange(storeAsUrl && nextSlug ? `${prefix}${nextSlug}${trailingSlash ? '/' : ''}` : nextSlug)
          }}
          placeholder={placeholder}
          required={required}
          variant="standard"
          fullWidth
          // Phase 14ad — disable browser/OS auto-capitalisation, autocorrect, and the
          // first-letter-uppercase virtual-keyboard nudge. URL slugs (LinkedIn / website)
          // are case-sensitive and overwhelmingly lower-case in practice; mobile keyboards
          // were silently capitalising the first letter on every keystroke before normalize-
          // Slug ran, which produced "Anwei-pan" instead of "anwei-pan" until the user
          // backspaced and retyped.
          inputProps={{
            autoCapitalize: 'off',
            autoCorrect: 'off',
            spellCheck: 'false',
            autoComplete: 'off',
            // The mobile keyboard "url" input mode skips the first-letter-uppercase
            // behaviour entirely on iOS/Android.
            inputMode: 'url',
          }}
          InputProps={{
            disableUnderline: true,
            sx: {
              px: 1.25,
              py: 0.7,
              fontFamily: 'monospace',
              fontSize: 15,
              minHeight: 44,
              alignItems: 'center',
            },
          }}
        />
      </Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={0.75}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        sx={{ mt: 0.75 }}
      >
        {helperText && (
          <Typography variant="caption" color="text.secondary">
            {helperText}
          </Typography>
        )}
        {preview && (
          <Chip
            size="small"
            variant="outlined"
            label={preview}
            sx={{
              maxWidth: '100%',
              '& .MuiChip-label': {
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            }}
          />
        )}
      </Stack>
    </Box>
  )
}

function BioSuggestionPanel({ suggestions, onUse }) {
  if (!suggestions || suggestions.length === 0) return null
  return (
    <Stack spacing={0.75}>
      {suggestions.slice(0, 3).map((text, index) => (
        <Button
          key={`bio-suggestion-${index}`}
          variant="text"
          onClick={() => onUse(text)}
          sx={{
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            gap: 1,
            p: 1,
            borderRadius: 1.5,
            color: 'text.primary',
            textAlign: 'left',
            border: '1px solid',
            borderColor: 'rgba(124,58,237,0.16)',
            backgroundColor: 'rgba(124,58,237,0.035)',
            textTransform: 'none',
            '&:hover': {
              borderColor: 'rgba(124,58,237,0.35)',
              backgroundColor: 'rgba(124,58,237,0.07)',
            },
          }}
        >
          <AutoAwesome sx={{ fontSize: 18, color: '#7c3aed', mt: 0.2, flexShrink: 0 }} />
          <Typography variant="caption" sx={{ flex: 1, lineHeight: 1.45 }}>
            {text}
          </Typography>
          <Chip label="Tab" size="small" sx={{ height: 20, fontSize: 11, flexShrink: 0 }} />
        </Button>
      ))}
    </Stack>
  )
}

function BioTextField({
  label,
  value,
  onChangeValue,
  minRows = 4,
  helperText,
  practitionerType,
  discipline,
  role,
  category,
}) {
  const suggestions = useMemo(
    () => buildBioSuggestions({ practitionerType, discipline, role, category }),
    [practitionerType, discipline, role, category],
  )
  const applySuggestion = (text) => {
    onChangeValue(value?.trim() ? `${value.trim()}\n\n${text}` : text)
  }
  return (
    <Stack spacing={1}>
      <TextField
        label={label}
        multiline
        minRows={minRows}
        value={value}
        onChange={(event) => onChangeValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Tab' && suggestions[0] && !event.shiftKey) {
            event.preventDefault()
            applySuggestion(suggestions[0])
          }
        }}
        helperText={helperText || `Example: ${suggestions[0] || ''}`}
        fullWidth
      />
      <BioSuggestionPanel suggestions={suggestions} onUse={applySuggestion} />
    </Stack>
  )
}

// Phase 14ac — canonical OG image used by every /apply route. The same prod media-CDN asset
// the email templates use, sized 1200×630-ish so WhatsApp / Twitter / LinkedIn previews
// render at full bleed.
const APPLY_OG_IMAGE = 'https://media.aumhum.com/images/prod/MEDIA-e8bd40c2af6e4d49b4d2aef2613c3234'

/**
 * Phase 14ac — apply-route meta tags. Builds title / description / OG / Twitter cards from
 * the route's identifying bits (type / discipline / role / sub-category) so link previews in
 * Slack, LinkedIn, Twitter, and JS-aware WhatsApp Business previews surface what the link is
 * actually for. Plain WhatsApp's link scraper doesn't execute JS — for that, the static OG
 * tags in index.html are the fallback. Long-term, server-side meta injection would close
 * the gap for non-JS scrapers; that's a CDN-level change tracked separately.
 */
function ApplyMeta({
  type,
  discipline,
  role,
  subCategory,
  applicantName,
  pageKind,
  fallbackTitle = 'Apply to AUMHUM',
  fallbackDescription = 'Join AUMHUM as a practitioner — doctor, therapist, screener, or psychology student. Complete a short application and our team will reach out.',
}) {
  const titleParts = []
  if (applicantName) {
    titleParts.push(applicantName)
  }
  if (role) {
    titleParts.push(`${role.charAt(0).toUpperCase() + role.slice(1)}`)
  } else if (type) {
    const typeLabel =
      TYPE_TILES.find((t) => t.id === type)?.label ||
      type.charAt(0).toUpperCase() + type.slice(1)
    titleParts.push(`Apply as ${typeLabel}`)
  }
  if (discipline) {
    titleParts.push(discipline.replace(/-/g, ' '))
  }
  if (subCategory) {
    titleParts.push(subCategory.charAt(0).toUpperCase() + subCategory.slice(1))
  }
  const subjectLine =
    titleParts.length > 0 ? titleParts.join(' · ') : fallbackTitle
  const title = pageKind === 'resume'
    ? 'Resume your AUMHUM application'
    : `${subjectLine} | AUMHUM`

  const descParts = []
  if (applicantName) {
    descParts.push(`${applicantName} — your invitation to AUMHUM is waiting.`)
  } else if (titleParts.length > 0) {
    descParts.push(`Apply to AUMHUM — ${titleParts.join(', ').toLowerCase()}.`)
  }
  descParts.push(
    pageKind === 'resume'
      ? 'Sign back in via email or WhatsApp OTP to keep editing your application.'
      : 'A few minutes to fill the form. Our team reviews + reaches out with next steps.',
  )
  const description = descParts.join(' ') || fallbackDescription

  const url = typeof window !== 'undefined' ? window.location.href : ''

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="AUMHUM" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={APPLY_OG_IMAGE} />
      {url && <meta property="og:url" content={url} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={APPLY_OG_IMAGE} />
      {/* Linked-data hint — Slack / LinkedIn use article:section + author when present. */}
      {type && <meta property="article:section" content={type} />}
      {discipline && <meta property="article:tag" content={discipline} />}
      {role && <meta property="article:tag" content={role} />}
    </Helmet>
  )
}

/**
 * Phase 14ak — banner shown at the top of every /apply screen when the applicant's browser
 * is concurrently signed in to AUMHUM as another account. Tells them clearly that the
 * application creates a SEPARATE account and offers a one-click Sign out for the existing
 * session if they prefer to clean up first.
 *
 * We never claim to know which user they are by name — the banner copy stays generic so
 * we don't fingerprint a token we may not have decoded reliably. Detection is purely
 * presence-based: any non-empty `token` in localStorage means "you're signed in elsewhere."
 *
 * The applicant can dismiss the banner for the rest of the apply session
 * (sessionStorage-scoped) so it doesn't keep nagging through Stage-1 → OTP → Stage-1.5
 * navigation. Refreshing the page brings it back if the existing token is still around.
 */
function ExistingSessionBanner() {
  const [hasExistingSession, setHasExistingSession] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      // The Phase 14e post-submit redirect to /mentor-home stamps a sessionStorage flag
      // before persisting the apply auth token to localStorage. If the flag is present,
      // the "existing" token in localStorage is THIS applicant's freshly-issued one — not
      // a pre-existing unrelated login — so the banner shouldn't show.
      const justSubmitted =
        window.sessionStorage.getItem('practitionerApplicationJustSubmitted') === '1'
      if (justSubmitted) return
      const dismissedFlag =
        window.sessionStorage.getItem('applyExistingSessionBannerDismissed') === '1'
      if (dismissedFlag) {
        setDismissed(true)
        return
      }
      const token = window.localStorage.getItem('token')
      setHasExistingSession(Boolean(token && token.trim()))
    } catch {
      /* localStorage access can throw in private mode — treat as "no session" */
    }
  }, [])

  function handleSignOut() {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.removeItem('token')
      window.localStorage.removeItem('authorities')
      window.sessionStorage.removeItem('token')
      window.sessionStorage.removeItem('authorities')
    } catch {
      /* non-fatal */
    }
    setHasExistingSession(false)
    // Reload so any in-memory auth state in other components / contexts also gets cleared.
    if (typeof window !== 'undefined' && typeof window.location?.reload === 'function') {
      window.location.reload()
    }
  }

  function handleDismiss() {
    try {
      window.sessionStorage.setItem('applyExistingSessionBannerDismissed', '1')
    } catch {
      /* non-fatal */
    }
    setDismissed(true)
  }

  if (!hasExistingSession || dismissed) return null

  return (
    <Alert
      severity="info"
      icon={<InfoOutlined />}
      sx={{
        mb: 2,
        borderRadius: 2,
        '& .MuiAlert-message': { width: '100%' },
      }}
    >
      <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
        You&apos;re currently signed in to AUMHUM
      </Typography>
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        This application creates a <strong>separate account</strong> using the email or
        mobile you enter below — your current session and its data won&apos;t be affected
        or used. After you submit, you&apos;ll sign in with your new applicant credentials.
      </Typography>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        useFlexGap
        flexWrap="wrap"
      >
        <Button
          variant="outlined"
          size="small"
          color="primary"
          onClick={handleSignOut}
        >
          Sign out of current account
        </Button>
        <Button
          variant="text"
          size="small"
          color="inherit"
          onClick={handleDismiss}
        >
          Continue as new applicant
        </Button>
      </Stack>
    </Alert>
  )
}

function ApplyShell({ children, title, subtitle, onBack }) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        background:
          'linear-gradient(180deg, #f3faf8 0%, #f8fafc 38%, #fff7ed 100%)',
        py: { xs: 3, md: 6 },
      }}
    >
      <Container maxWidth="md">
        {/* Phase 14u — centered AUMHUM brand mark above the card so the public /apply
            surfaces look consistent with the rest of the auth-style touchpoints. */}
        <Box sx={{ display: 'grid', placeItems: 'center', mb: 2 }}>
          <Box
            component="img"
            src={APPLY_LOGO_URL}
            alt="AUMHUM"
            sx={{
              height: { xs: 44, md: 56 },
              width: 'auto',
              borderRadius: 2,
              display: 'block',
            }}
          />
        </Box>
        {/* Phase 14z — the card now carries a coloured top stripe + soft drop-shadow so it
            reads as the primary content rather than a flat panel. The stripe colour comes
            from the same brand palette (#7c3aed → coral) used in the email templates. */}
        <Paper
          sx={{
            position: 'relative',
            p: { xs: 2, md: 4 },
            borderRadius: 3,
            boxShadow:
              '0 1px 3px rgba(15,23,42,0.06), 0 14px 40px -8px rgba(15,23,42,0.10)',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background:
                'linear-gradient(90deg, #1e3151 0%, #7c3aed 45%, #ec8074 100%)',
            },
          }}
        >
          {onBack && (
            <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
              <IconButton onClick={onBack} size="small">
                <ArrowBack fontSize="small" />
              </IconButton>
              <Typography variant="caption" color="text.secondary">
                Back
              </Typography>
            </Stack>
          )}
          {/* Phase 14ak — surfaces the existing-AUMHUM-session caveat ONCE per apply
              session, before the form even renders. Self-hides when no token in
              localStorage / when the post-submit redirect just landed here. */}
          <ExistingSessionBanner />
          {title && (
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {subtitle}
            </Typography>
          )}
          {children}
        </Paper>
      </Container>
    </Box>
  )
}

/**
 * Phase 14z — profile-photo guidelines dialog. Same Approved / Not-Approved structure as the
 * print poster the support team uses to review photos: 4 approved patterns on the left,
 * 4 anti-patterns on the right, with a soft "thank-you" footer. We render the content as
 * styled cards with descriptive headings (instead of literal example photos) so the dialog
 * stays sharp on every screen size and renders without external image assets.
 */
/**
 * Phase 14ad — drawer wrapper for legal documents (Terms of Service, Privacy Policy,
 * additional agreements). Renders the document URL inside an <iframe> so the applicant
 * stays on the apply flow instead of bouncing to a new tab. The anchor responds to
 * viewport size:
 *   - desktop (sm+) → right edge, slides left.
 *   - mobile (xs)   → bottom edge, slides up (closer to where the thumb sits).
 *
 * The header carries an "Open in new tab" affordance for users who want a permanent
 * window, plus a close button.
 */
function LegalDocDrawer({ open, onClose, url, title }) {
  // theme.breakpoints.down('sm') is true for xs only — keeps tablet+ on the right anchor.
  const isMobile = useMediaQuery('(max-width: 600px)')
  const anchor = isMobile ? 'bottom' : 'right'
  return (
    <Drawer
      anchor={anchor}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 720, md: 820 },
          maxWidth: '100%',
          height: { xs: '90vh', sm: '100%' },
          borderTopLeftRadius: { xs: 16, sm: 0 },
          borderTopRightRadius: { xs: 16, sm: 0 },
          display: 'flex',
          flexDirection: 'column',
        },
      }}
      // Phase 14v — sit above the parent dialog/modal so the drawer is visible when the
      // caller is itself a modal stack (consent panel inside the post-login form, etc.).
      sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      ModalProps={{ sx: { zIndex: (theme) => theme.zIndex.modal + 2 } }}
    >
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {url}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5}>
          {url && (
            <Tooltip title="Open in a new tab">
              <IconButton
                size="small"
                onClick={() => {
                  if (typeof window !== 'undefined') {
                    window.open(url, '_blank', 'noopener,noreferrer')
                  }
                }}
              >
                <OpenInNew fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <IconButton size="small" onClick={onClose} aria-label="close">
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {url ? (
          <iframe
            src={url}
            title={title || 'Legal document'}
            style={{ width: '100%', height: '100%', border: 0 }}
          />
        ) : (
          <Box sx={{ display: 'grid', placeItems: 'center', height: '100%' }}>
            <CircularProgress size={28} />
          </Box>
        )}
      </Box>
    </Drawer>
  )
}

function ProfilePhotoGuidelinesDialog({ open, onClose }) {
  const APPROVED = [
    {
      title: 'Clear face, good lighting',
      detail:
        'Natural light or a well-lit room. Face takes up most of the frame and is sharply in focus.',
    },
    {
      title: 'Professional and approachable',
      detail:
        'Smart-casual or business attire. Warm, calm expression — what a patient sees on first contact.',
    },
    {
      title: 'Plain background, proper framing',
      detail:
        'Solid wall or simple backdrop. Head + shoulders centered; eyes in the upper third of the frame.',
    },
    {
      title: 'Recent, high-quality solo photo',
      detail:
        'Taken in the last year. Solo portrait, sharp focus, true colours; no heavy retouching.',
    },
  ]
  const NOT_APPROVED = [
    {
      title: 'Blurry or poorly lit',
      detail:
        'Mirror selfies, low-light shots, motion blur, or pictures taken through a window or screen.',
    },
    {
      title: 'Group photo',
      detail:
        'Even cropped from a group shot, the energy and framing are wrong. Use a dedicated solo portrait.',
    },
    {
      title: 'Face not clearly visible',
      detail:
        'Too far from the camera, head turned away, hair / mask covering features, or a body-only shot.',
    },
    {
      title: 'Filters, sunglasses, or distractions',
      detail:
        'Heavy filters, sunglasses, hats covering the face, or busy/touristic backgrounds that pull focus.',
    },
  ]
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <VerifiedUser sx={{ color: '#7c3aed' }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Profile Photo Guidelines
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Approved vs Not Approved — a few minutes here saves a back-and-forth later.
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
          }}
        >
          {/* Approved column */}
          <Stack spacing={1.25}>
            {APPROVED.map((item) => (
              <Paper
                key={`ok-${item.title}`}
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  borderColor: 'rgba(16, 185, 129, 0.4)',
                  background: 'rgba(16, 185, 129, 0.05)',
                }}
              >
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <CheckCircle sx={{ color: 'rgb(16, 185, 129)', mt: 0.25 }} />
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, color: 'rgb(15, 118, 110)' }}
                    >
                      Approved · {item.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.detail}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Stack>
          {/* Not-approved column */}
          <Stack spacing={1.25}>
            {NOT_APPROVED.map((item) => (
              <Paper
                key={`bad-${item.title}`}
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  borderColor: 'rgba(239, 68, 68, 0.4)',
                  background: 'rgba(239, 68, 68, 0.04)',
                }}
              >
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <HighlightOff sx={{ color: 'rgb(239, 68, 68)', mt: 0.25 }} />
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, color: 'rgb(153, 27, 27)' }}
                    >
                      Not Approved · {item.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.detail}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Box>
        <Alert severity="info" sx={{ mt: 2 }} icon={<VerifiedUser fontSize="small" />}>
          A professional profile photo helps clients feel confident in connecting with you.
          Thank you for presenting your best, professional self.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function TilesGrid({ tiles, onPick }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
      }}
    >
      {tiles.map(({ id, label, description, Icon, gradient }) => (
        <Paper
          key={id}
          elevation={0}
          onClick={() => onPick(id)}
          sx={{
            p: 3,
            borderRadius: 3,
            cursor: 'pointer',
            background: gradient,
            color: 'white',
            transition: 'transform 0.15s ease, box-shadow 0.15s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
            },
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'rgba(255,255,255,0.15)',
              mb: 1.5,
            }}
          >
            <Icon sx={{ fontSize: 28, color: 'white' }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {label}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
            {description}
          </Typography>
        </Paper>
      ))}
    </Box>
  )
}

function ApplyLanding() {
  const navigate = useNavigate()
  return (
    <ApplyShell
      title="Apply to join AUMHUM"
      subtitle="Pick what describes you best. We'll ask only what's needed for that path; once we review your application we'll be in touch with next steps."
    >
      <ApplyMeta
        pageKind="landing"
        fallbackTitle="Apply to AUMHUM as a practitioner"
        fallbackDescription="Join AUMHUM as a doctor, mental-health professional, screener, or psychology student. Pick a path and apply in a few minutes."
      />
      <TilesGrid
        tiles={TYPE_TILES}
        onPick={(id) => {
          if (id === 'mhp') navigate('/apply/mhp')
          else if (id === 'doctor') navigate('/apply/doctor')
          else navigate(`/apply/${id}`)
        }}
      />
    </ApplyShell>
  )
}

function ApplyMhpRolePicker() {
  const navigate = useNavigate()
  return (
    <ApplyShell
      title="Mental Health Professional"
      subtitle="Pick the role you practise. We'll tailor the questions to your training and credentials."
      onBack={() => navigate('/apply')}
    >
      <ApplyMeta
        pageKind="mhp-role-picker"
        type="therapist"
        fallbackTitle="Apply as a Mental Health Professional"
        fallbackDescription="Apply to AUMHUM as a psychologist, psychotherapist, counsellor, or nutritionist. Pick a role to start."
      />
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
        }}
      >
        {MHP_ROLE_TILES.map((tile) => (
          <Paper
            key={tile.id}
            variant="outlined"
            onClick={() => navigate(`/apply/mhp/${tile.id}`)}
            sx={{
              p: 3,
              borderRadius: 3,
              cursor: 'pointer',
              transition: 'border-color 0.15s ease',
              '&:hover': { borderColor: 'primary.main' },
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {tile.label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Discipline: {tile.discipline.replace(/-/g, ' ')}
            </Typography>
          </Paper>
        ))}
      </Box>
    </ApplyShell>
  )
}

/**
 * Application form component shared by:
 *   - /apply/:type          (organic, basic mode)
 *   - /apply/mhp/:role      (organic MHP, basic mode with role pre-set)
 *   - /apply/i/:token       (invitation, rich mode)
 * The mode prop drives whether registration IDs and specialization pickers are shown.
 */
function ApplicationForm({
  mode,                  // 'basic' | 'rich'
  practitionerType,
  discipline,
  role,
  subCategoryOptions,
  invitation,            // { token, prefillFullName, prefillEmail, prefillMobile, ... } when invited
  onCancel,
  onSubmitted,
}) {
  const [config, setConfig] = useState({ otpDisabled: false, loading: true })
  const [form, setForm] = useState(() => ({
    fullName: invitation?.prefillFullName || '',
    slug: '',
    email: invitation?.prefillEmail || '',
    mobile: invitation?.prefillMobile || '',
    shortBio: '',
    subCategory: invitation?.subCategory || '',
    qualifications: [],
    registrationIds: [],
    specializations: [],
    emailOtpVerified: false,
    mobileOtpVerified: false,
    emailVerificationSessionId: null,
    mobileVerificationSessionId: null,
    // Phase 14u — LinkedIn captured on Stage 1 so the support team has it from the moment
    // the application lands; the post-OTP enrichment screen can edit it later.
    linkedinProfileUrl: '',
  }))
  const [otpEmailSent, setOtpEmailSent] = useState(false)
  const [otpMobileSent, setOtpMobileSent] = useState(false)
  const [emailOtpCode, setEmailOtpCode] = useState('')
  const [mobileOtpCode, setMobileOtpCode] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submittedSummary, setSubmittedSummary] = useState(null)
  // Phase 14s — qualification + specialization catalogs for the discipline. Populated lazily
  // from the public catalog endpoint when the form has a discipline (organic flow on a known
  // tile, or invitation flow with a discipline pre-set). Falls back to free-text input when
  // the catalog isn't available (no discipline selected, or fetch fails).
  const [qualificationCatalog, setQualificationCatalog] = useState(null)
  const [specializationCatalog, setSpecializationCatalog] = useState(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await applyAnonAxios.get(`${PUBLIC_BASE}/config`)
        if (!cancelled) {
          const data = res?.data?.data || res?.data
          setConfig({ otpDisabled: Boolean(data?.otpDisabled), loading: false })
        }
      } catch {
        if (!cancelled) setConfig({ otpDisabled: false, loading: false })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  // Phase 14s — fetch the discipline-aware qualification + specialization catalogs whenever
  // the discipline changes. Only therapist disciplines have a catalog wired today
  // (psychotherapy, nutrition-consultation); for doctors / screeners the catalog stays null
  // and the form falls back to free-text qualification entry.
  useEffect(() => {
    if (!discipline) {
      setQualificationCatalog(null)
      setSpecializationCatalog(null)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const [qualRes, specRes] = await Promise.all([
          applyAnonAxios.get(`${PUBLIC_BASE}/catalog/qualifications`, {
            params: { discipline },
          }),
          applyAnonAxios.get(`${PUBLIC_BASE}/catalog/specializations`, {
            params: { discipline },
          }),
        ])
        if (cancelled) return
        const qualData = qualRes?.data?.data || qualRes?.data
        const specData = specRes?.data?.data || specRes?.data
        setQualificationCatalog(qualData?.qualificationCatalog || null)
        setSpecializationCatalog(specData?.specializationCatalog || null)
      } catch {
        if (!cancelled) {
          setQualificationCatalog(null)
          setSpecializationCatalog(null)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [discipline])

  async function sendOtp(channel) {
    const value = channel === 'email' ? form.email : form.mobile
    if (!value) {
      toast.error(`Enter your ${channel} first.`)
      return
    }
    try {
      // Server routes mobile→WhatsApp by default + email→email; the optional dispatchChannel
      // override (WHATSAPP / OUTCALL / EMAIL / AUTO) is left to the server's pluggable layer.
      const recaptchaToken = await fetchRecaptchaToken('apply_otp_send')
      const res = await applyAnonAxios.post(`${PUBLIC_BASE}/otp/send`, {
        channel,
        value,
        recaptchaToken,
      })
      const data = res?.data?.data || res?.data
      if (data?.disabled) {
        // Bypass mode — mark as verified immediately, no session id required server-side either.
        setForm((p) => ({
          ...p,
          [channel === 'email' ? 'emailOtpVerified' : 'mobileOtpVerified']: true,
        }))
        toast.info('OTP gating disabled — proceeding without verification.')
      } else if (data?.sent && data?.sessionId) {
        // Stash the sessionId — the submit endpoint validates it server-side instead of trusting
        // a client boolean. Without a session id the submit will be rejected.
        setForm((p) => ({
          ...p,
          ...(channel === 'email'
            ? { emailVerificationSessionId: data.sessionId, emailOtpVerified: false }
            : { mobileVerificationSessionId: data.sessionId, mobileOtpVerified: false }),
        }))
        if (channel === 'email') setOtpEmailSent(true)
        else setOtpMobileSent(true)
        toast.success(`OTP sent to your ${channel}.`)
      } else {
        toast.error('OTP integration pending — try again later.')
      }
    } catch (err) {
      const message = err?.response?.data?.message || 'Could not send OTP.'
      toast.error(message)
    }
  }

  async function verifyOtp(channel) {
    const otp = channel === 'email' ? emailOtpCode : mobileOtpCode
    const sessionId =
      channel === 'email' ? form.emailVerificationSessionId : form.mobileVerificationSessionId
    if (!otp) {
      toast.error('Enter the code first.')
      return
    }
    if (!sessionId) {
      toast.error('Send the OTP first.')
      return
    }
    try {
      const recaptchaToken = await fetchRecaptchaToken('apply_otp_verify')
      const res = await applyAnonAxios.post(`${PUBLIC_BASE}/otp/verify`, {
        sessionId,
        otp,
        recaptchaToken,
      })
      const data = res?.data?.data || res?.data
      if (data?.verified) {
        setForm((p) => ({
          ...p,
          [channel === 'email' ? 'emailOtpVerified' : 'mobileOtpVerified']: true,
        }))
        toast.success(`${channel} verified.`)
      } else {
        toast.error('Invalid code.')
      }
    } catch (err) {
      const message = err?.response?.data?.message || 'Could not verify code.'
      toast.error(message)
    }
  }

  const otpRequired = !config.otpDisabled
  const emailVerified = form.emailOtpVerified || (config.otpDisabled && !!form.email)
  const mobileVerified = form.mobileOtpVerified || (config.otpDisabled && !!form.mobile)

  async function handleSubmit() {
    if (!form.fullName.trim()) {
      toast.error('Full name is required.')
      return
    }
    if (!form.email.trim() && !form.mobile.trim()) {
      toast.error('Email or mobile is required.')
      return
    }
    if (otpRequired) {
      if (form.email && !form.emailOtpVerified) {
        toast.error('Verify your email with the OTP first.')
        return
      }
      if (form.mobile && !form.mobileOtpVerified) {
        toast.error('Verify your mobile with the OTP first.')
        return
      }
    }
    setSubmitting(true)
    try {
      const payload = {
        practitionerType,
        discipline: discipline || undefined,
        role: role || undefined,
        subCategory: form.subCategory || undefined,
        fullName: form.fullName.trim(),
        slug: (form.slug || suggestedSlugFromName(form.fullName, role || practitionerType)).trim() || undefined,
        email: form.email.trim() || undefined,
        mobile: form.mobile.trim() || undefined,
        shortBio: form.shortBio.trim() || undefined,
        linkedinProfileUrl: form.linkedinProfileUrl.trim() || undefined,
        qualifications: form.qualifications,
        registrationIds: form.registrationIds.filter(
          (r) => r.typeId?.trim() && r.id?.trim(),
        ),
        specializations: form.specializations,
        // Session IDs (not booleans) — server looks each session up and confirms it's verified
        // for the matching contact value. Bypass mode lets these be undefined.
        emailVerificationSessionId: form.emailVerificationSessionId || undefined,
        mobileVerificationSessionId: form.mobileVerificationSessionId || undefined,
        invitationToken: invitation?.token || undefined,
      }
      const recaptchaToken = await fetchRecaptchaToken('apply_submit')
      const res = await applyAnonAxios.post(`${PUBLIC_BASE}/submit`, {
        ...payload,
        recaptchaToken,
      })
      const data = res?.data?.data || res?.data
      const summary = data?.application || data
      toast.success('Application submitted.')
      setSubmittedSummary(summary)
      onSubmitted?.(summary)
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || 'Could not submit application.'
      toast.error(message)
    } finally {
      setSubmitting(false)
    }
  }

  // Phase 14s — invitation flow renders its type / discipline / role / sub-category as locked
  // chips at the top of the form so the applicant can see what they're applying for but can't
  // accidentally change it. The support team picks these on the Generate Invitation dialog and
  // the applicant inherits them — re-typing them on /apply would let invitations drift away
  // from the support team's intent.
  const invitationChips = useMemo(() => {
    if (!invitation) return []
    const chips = []
    if (practitionerType) {
      const label =
        TYPE_TILES.find((t) => t.id === practitionerType)?.label ||
        practitionerType.charAt(0).toUpperCase() + practitionerType.slice(1)
      chips.push({ label: `Type: ${label}`, color: 'primary' })
    }
    if (discipline) {
      chips.push({
        label: `Discipline: ${discipline.replace(/-/g, ' ')}`,
        color: 'info',
      })
    }
    if (role) {
      chips.push({
        label: `Role: ${role.charAt(0).toUpperCase() + role.slice(1)}`,
        color: 'info',
      })
    }
    if (invitation?.subCategory) {
      chips.push({
        label: `Sub-category: ${invitation.subCategory.charAt(0).toUpperCase() + invitation.subCategory.slice(1)}`,
        color: 'default',
      })
    }
    return chips
  }, [invitation, practitionerType, discipline, role])

  // Phase 14t — once submitted, hand off to the post-login enrichment screen when the server
  // returned an auth token. Falls back to the old "thank you" card when no token is present
  // (e.g. organic basic flow on hosts where token issuance failed) so we never strand users.
  if (submittedSummary) {
    if (submittedSummary?.authToken) {
      return (
        // Phase 14w — wrap the post-login form in an ErrorBoundary so a render glitch
        // (e.g. malformed hydration response) doesn't blank the page; the user falls back
        // to the resume flow with a clear next step.
        <PostLoginEnrichmentBoundary
          contactEmail={form.email}
          referenceId={submittedSummary?.practitionerId}
        >
          <PostLoginEnrichmentForm
            authToken={submittedSummary.authToken}
            summary={submittedSummary}
            practitionerType={practitionerType}
            discipline={discipline}
            role={role}
            qualificationCatalog={qualificationCatalog}
            specializationCatalog={specializationCatalog}
            pendingLinkedin={form.linkedinProfileUrl}
          />
        </PostLoginEnrichmentBoundary>
      )
    }
    return (
      <ApplyShell
        title="Application received"
        subtitle="Thanks. Our team will review your application and reach out with next steps. Keep an eye on your email or mobile for the follow-up."
      >
        <Alert severity="success" sx={{ mb: 2 }}>
          We have created your application record. Reference id:{' '}
          <strong>{submittedSummary?.practitionerId}</strong>
        </Alert>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
          <Chip label={`Type: ${submittedSummary?.practitionerType}`} variant="outlined" />
          {submittedSummary?.invitationConsumed && (
            <Chip label="Invitation consumed" color="success" variant="outlined" />
          )}
        </Stack>
        {/* Phase 14w — give the applicant a clear way to come back. The invitation link is
            now consumed so reloading /apply/i/<token> won't help; sign-in with email-OTP
            is how they re-enter the enrichment form. */}
        <Alert severity="info" icon={false}>
          To keep editing your application later (bio, qualifications, recent fees, resume,
          etc.), bookmark this link:{' '}
          <a
            href={`/apply/resume?email=${encodeURIComponent(form.email || '')}`}
            style={{ textDecoration: 'none', fontWeight: 600 }}
          >
            /apply/resume
          </a>
          . When you come back, we will email you a sign-in code.
        </Alert>
      </ApplyShell>
    )
  }

  const invitationLocksSubCategory = Boolean(invitation && invitation.subCategory)
  // Phase 14ai — slug auto-derived server-side; no longer rendered in the form.
  const bioCategory = form.subCategory || invitation?.subCategory || ''

  return (
    <Stack spacing={2}>
      {!otpRequired && (
        <Alert severity="info" sx={{ mt: 1 }}>
          OTP verification is currently disabled (testing mode). Your email and mobile will be
          accepted as-is.
        </Alert>
      )}

      {/* Phase 14s — locked invitation context. The applicant sees what the support team
          recorded on the invitation; they can't accidentally re-pick a different role. */}
      {invitationChips.length > 0 && (
        <Paper
          variant="outlined"
          sx={{ p: 1.5, borderRadius: 2, background: 'rgba(124,58,237,0.04)' }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 0.75, fontWeight: 600 }}
          >
            Selected for you on this invitation
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {invitationChips.map((c) => (
              <Chip
                key={c.label}
                label={c.label}
                size="small"
                color={c.color}
                variant="outlined"
              />
            ))}
          </Stack>
        </Paper>
      )}

      <FieldSection
        title="Profile basics"
        caption="This is what support sees first, and what clients will eventually recognise."
        icon={Badge}
        accent="identity"
      >
        <Stack spacing={1.5}>
          <TextField
            label="Full name"
            value={form.fullName}
            onChange={(e) => setForm((p) => ({ ...p, fullName: e.target.value }))}
            required
            fullWidth
          />
          {/* Phase 14ai — public-profile slug is no longer applicant-editable on the /apply
              form. The server auto-generates a unique slug from the applicant's full name on
              submit (slugService.getNewSlug appends a counter suffix when needed); support can
              still edit the slug from the support-portal onboarding page if a manual override
              is needed. Removing this field keeps the apply form focused on identity / role /
              contact and avoids the small but real failure mode of an applicant typing a
              taken slug, hitting "taken", and bouncing. */}
          {/* Phase 14s — show the sub-category picker only when the invitation didn't already
              freeze it (organic flow on a known role). Invitation flows render the sub-category
              inside the locked chips block above instead of repeating it as an editable field. */}
          {!invitationLocksSubCategory &&
            subCategoryOptions &&
            subCategoryOptions.length > 0 && (
              <Autocomplete
                options={subCategoryOptions}
                value={form.subCategory || null}
                onChange={(_e, val) => setForm((p) => ({ ...p, subCategory: val || '' }))}
                freeSolo
                renderInput={(params) => (
                  <TextField {...params} label="Sub-category (optional)" />
                )}
              />
            )}
          <BioTextField
            label="A short bio about yourself"
            minRows={3}
            value={form.shortBio}
            onChangeValue={(shortBio) => setForm((p) => ({ ...p, shortBio }))}
            helperText="Use Tab while focused here to drop in the first suggested sentence."
            practitionerType={practitionerType}
            discipline={discipline}
            role={role}
            category={bioCategory}
          />
        </Stack>
      </FieldSection>

      <FieldSection
        title="Contact verification"
        caption="Use whichever channel is easiest; OTP keeps the application tied to you."
        icon={AlternateEmail}
        accent="contact"
      >
        <Stack spacing={1.25}>
          {/* Email + OTP */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => {
                setForm((p) => ({
                  ...p,
                  email: e.target.value,
                  emailOtpVerified: false,
                  emailVerificationSessionId: null,
                }))
                setOtpEmailSent(false)
                setEmailOtpCode('')
              }}
              fullWidth
              disabled={emailVerified}
            />
            {otpRequired && form.email && !emailVerified && !otpEmailSent && (
              <Button variant="outlined" onClick={() => sendOtp('email')} sx={{ flexShrink: 0 }}>
                Send OTP
              </Button>
            )}
            {emailVerified && <Chip color="success" label="Email verified" sx={{ flexShrink: 0 }} />}
          </Stack>
          {otpRequired && otpEmailSent && !emailVerified && (
            <Stack direction="row" spacing={1}>
              <TextField
                label="Email OTP"
                value={emailOtpCode}
                onChange={(e) => setEmailOtpCode(e.target.value)}
                size="small"
              />
              <Button variant="contained" size="small" onClick={() => verifyOtp('email')}>
                Verify
              </Button>
            </Stack>
          )}

          {/* Mobile + OTP */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              label="Mobile"
              value={form.mobile}
              onChange={(e) => {
                setForm((p) => ({
                  ...p,
                  mobile: e.target.value,
                  mobileOtpVerified: false,
                  mobileVerificationSessionId: null,
                }))
                setOtpMobileSent(false)
                setMobileOtpCode('')
              }}
              fullWidth
              disabled={mobileVerified}
            />
            {otpRequired && form.mobile && !mobileVerified && !otpMobileSent && (
              <Button variant="outlined" onClick={() => sendOtp('mobile')} sx={{ flexShrink: 0 }}>
                Send OTP
              </Button>
            )}
            {mobileVerified && <Chip color="success" label="Mobile verified" sx={{ flexShrink: 0 }} />}
          </Stack>
          {otpRequired && otpMobileSent && !mobileVerified && (
            <Stack direction="row" spacing={1}>
              <TextField
                label="Mobile OTP"
                value={mobileOtpCode}
                onChange={(e) => setMobileOtpCode(e.target.value)}
                size="small"
              />
              <Button variant="contained" size="small" onClick={() => verifyOtp('mobile')}>
                Verify
              </Button>
            </Stack>
          )}
        </Stack>
      </FieldSection>

      <FieldSection
        title="Professional links"
        caption="Clean URL fields make it obvious which part you should edit."
        icon={LinkIcon}
        accent="links"
      >
        <UrlSlugField
          label="LinkedIn"
          prefix={LINKEDIN_PROFILE_URL_PREFIX}
          value={form.linkedinProfileUrl}
          onChange={(linkedinProfileUrl) =>
            setForm((p) => ({ ...p, linkedinProfileUrl }))
          }
          placeholder="ananya-rao"
          helperText="Paste a full LinkedIn URL or type only your /in/ slug."
          normalizeSlug={sanitizeLinkedinSlug}
          storeAsUrl
          trailingSlash
        />
      </FieldSection>

      <FieldSection
        title="Training and credentials"
        caption="Qualifications, focus areas, and registrations sit together for easier review."
        icon={WorkspacePremium}
        accent="credentials"
      >
        <Stack spacing={1.5}>
      {/* Phase 14s — qualifications dropdown sourced from the discipline-aware public catalog.
          Falls back to free-text entry when the catalog hasn't loaded (no discipline yet on
          the organic /apply path) so the form still works for doctors / screeners. */}
      {qualificationCatalog &&
      ((qualificationCatalog.preferred && qualificationCatalog.preferred.length > 0) ||
        (qualificationCatalog.fallback && qualificationCatalog.fallback.length > 0)) ? (
        (() => {
          const grouped = [
            ...(qualificationCatalog.preferred || []).map((item) => ({
              id: item.id || item.label,
              label: item.label,
              group:
                'Suggested for ' +
                (qualificationCatalog.displayLabel ||
                  (discipline || '').replace(/-/g, ' ')),
            })),
            ...(qualificationCatalog.fallback || []).map((item) => ({
              id: item.longTitle || item.title || item.id,
              label: item.longTitle || item.title || item.id,
              group: 'Other',
            })),
          ]
          return (
            <Autocomplete
              multiple
              freeSolo
              options={grouped}
              groupBy={(option) => option.group}
              getOptionLabel={(option) =>
                typeof option === 'string'
                  ? option
                  : option?.label || option?.id || ''
              }
              isOptionEqualToValue={(option, value) =>
                (typeof option === 'string' ? option : option.label) ===
                (typeof value === 'string' ? value : value.label)
              }
              value={form.qualifications}
              onChange={(_e, nextValue) =>
                setForm((p) => ({
                  ...p,
                  qualifications: nextValue
                    .map((v) => (typeof v === 'string' ? v : v.label))
                    .filter(Boolean),
                }))
              }
              renderTags={(value, getTagProps) =>
                value.map((label, index) => (
                  <Chip
                    label={label}
                    size="small"
                    {...getTagProps({ index })}
                    key={`${label}-${index}`}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Qualifications"
                  helperText={`Pick from the curated list for ${
                    qualificationCatalog.displayLabel ||
                    (discipline || '').replace(/-/g, ' ')
                  }, or type a custom value.`}
                />
              )}
            />
          )
        })()
      ) : (
        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={form.qualifications}
          onChange={(_e, val) => setForm((p) => ({ ...p, qualifications: val }))}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Qualifications"
              helperText="Type each qualification and press Enter (e.g. M.A. Clinical Psychology)."
            />
          )}
        />
      )}

      {/* Phase 14s — specializations picker (rich/invitation flow only) sourced from the
          public catalog. Renders only when we have a populated catalog for the discipline. */}
      {mode === 'rich' &&
        specializationCatalog &&
        ((specializationCatalog.preferred && specializationCatalog.preferred.length > 0) ||
          (specializationCatalog.fallback && specializationCatalog.fallback.length > 0)) && (
          (() => {
            const grouped = [
              ...(specializationCatalog.preferred || []).map((item) => ({
                id: item.id || item.label,
                label: item.label || item.title || item.id,
                group:
                  'Suggested for ' +
                  (specializationCatalog.displayLabel ||
                    (discipline || '').replace(/-/g, ' ')),
              })),
              ...(specializationCatalog.fallback || []).map((item) => ({
                id: item.id || item.title,
                label: item.title || item.id,
                group: 'Other',
              })),
            ]
            return (
              <Autocomplete
                multiple
                freeSolo
                options={grouped}
                groupBy={(option) => option.group}
                getOptionLabel={(option) =>
                  typeof option === 'string'
                    ? option
                    : option?.label || option?.id || ''
                }
                isOptionEqualToValue={(option, value) =>
                  (typeof option === 'string' ? option : option.label) ===
                  (typeof value === 'string' ? value : value.label)
                }
                value={form.specializations}
                onChange={(_e, nextValue) =>
                  setForm((p) => ({
                    ...p,
                    specializations: nextValue
                      .map((v) => (typeof v === 'string' ? v : v.label))
                      .filter(Boolean),
                  }))
                }
                renderTags={(value, getTagProps) =>
                  value.map((label, index) => (
                    <Chip
                      label={label}
                      size="small"
                      {...getTagProps({ index })}
                      key={`${label}-${index}`}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Specializations"
                    helperText={`Pick from the curated list for ${
                      specializationCatalog.displayLabel ||
                      (discipline || '').replace(/-/g, ' ')
                    }, or type a custom value.`}
                  />
                )}
              />
            )
          })()
        )}

      {mode === 'rich' && (
        <>
          <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 700 }}>
            Registration / license IDs
          </Typography>
          {form.registrationIds.map((reg, idx) => (
            <Stack
              key={`reg-${idx}`}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems="center"
            >
              <TextField
                label="Type"
                placeholder="RCI / MCI / IDA"
                value={reg.typeId || ''}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    registrationIds: p.registrationIds.map((r, i) =>
                      i === idx ? { ...r, typeId: e.target.value } : r,
                    ),
                  }))
                }
                fullWidth
              />
              <TextField
                label="Number"
                value={reg.id || ''}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    registrationIds: p.registrationIds.map((r, i) =>
                      i === idx ? { ...r, id: e.target.value } : r,
                    ),
                  }))
                }
                fullWidth
              />
              <Button
                color="error"
                onClick={() =>
                  setForm((p) => ({
                    ...p,
                    registrationIds: p.registrationIds.filter((_r, i) => i !== idx),
                  }))
                }
              >
                Remove
              </Button>
            </Stack>
          ))}
          <Button
            variant="outlined"
            onClick={() =>
              setForm((p) => ({
                ...p,
                registrationIds: [...p.registrationIds, { typeId: '', id: '' }],
              }))
            }
          >
            Add registration ID
          </Button>
        </>
      )}
        </Stack>
      </FieldSection>

      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
        {onCancel && <Button onClick={onCancel}>Cancel</Button>}
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Submitting…' : 'Submit application'}
        </Button>
      </Stack>
    </Stack>
  )
}

/**
 * Phase 14w — error boundary so a render-time crash inside PostLoginEnrichmentForm doesn't
 * blank the entire /apply tab and strand the applicant. Pre-Phase-14w bug repro: a malformed
 * /enrichment response would throw inside the hydration mapper and React would unmount the
 * subtree silently, leaving the page blank with no recovery path.
 */
class PostLoginEnrichmentBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false }
  }
  static getDerivedStateFromError() {
    return { hasError: true }
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('PostLoginEnrichmentForm crashed:', error, info)
  }
  render() {
    if (this.state.hasError) {
      const { contactEmail, referenceId } = this.props
      return (
        <ApplyShell
          title="We hit a snag"
          subtitle="Your application is saved — but the editor below crashed. Sign back in to keep editing; nothing was lost."
        >
          <Alert severity="warning" sx={{ mb: 2 }}>
            Your application reference is <strong>{referenceId || 'on file'}</strong>. The
            inline editor failed to render in this browser; signing back in via email
            usually clears the issue.
          </Alert>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              onClick={() => {
                window.location.href = `/apply/resume${
                  contactEmail ? `?email=${encodeURIComponent(contactEmail)}` : ''
                }`
              }}
            >
              Sign in to continue
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                window.location.reload()
              }}
            >
              Reload this page
            </Button>
          </Stack>
        </ApplyShell>
      )
    }
    return this.props.children
  }
}

/**
 * Phase 14t — post-login enrichment form. Renders inline on /apply right after the basic
 * Stage 1 submission succeeds and returns an auth token. The applicant can fill the same
 * fields the support-portal Stage 2 page would otherwise leave blank: bio, qualifications,
 * specializations, registration IDs, profile photo, and the mandatory credential document.
 *
 * Each section saves to /api/secure/practitioner-self/enrichment with PATCH semantics, so
 * partial saves are durable across page reloads. The auth token only survives the current
 * tab session — we don't persist it to localStorage to avoid leaving a long-lived
 * unattended session token sitting on a public-facing /apply origin.
 */
function PostLoginEnrichmentForm({
  authToken,
  summary,
  practitionerType,
  discipline,
  role,
  qualificationCatalog,
  specializationCatalog,
  pendingLinkedin,
}) {
  const [hydrated, setHydrated] = useState(null)
  const [hydrating, setHydrating] = useState(true)
  const [saving, setSaving] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  // Phase 14z — Profile photo guidelines dialog state + signature canvas state + ToS/Privacy
  // checkbox state + cached audit context. Audit context is captured once on mount and
  // refreshed on Submit-for-review so the timestamps line up with the actual consent moment.
  const [photoGuidelinesOpen, setPhotoGuidelinesOpen] = useState(false)
  const [signatureMediaId, setSignatureMediaId] = useState('')
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false)
  // Phase 14ad — legal-doc drawer state. Holds { url, title } when a legal link is open;
  // null when the drawer is closed. Used by ToS, Privacy, and "Read full agreement" links
  // so the applicant never leaves the apply flow.
  const [legalDoc, setLegalDoc] = useState(null)
  const auditContextRef = useRef(null)
  const [form, setForm] = useState({
    practitionerSlug: summary?.practitionerSlug || '',
    shortBio: '',
    qualifications: [],
    specializations: [],
    registrationIds: [],
    profilePicId: '',
    mandatoryCredentialDocFileId: '',
    mandatoryCredentialDocTitle: '',
    // Phase 14u — applicant-only fields persisted on ApplicantDetails.
    linkedinProfileUrl: pendingLinkedin || '',
    hasOtherAffiliations: false,
    currentAffiliations: [],
    // Three fixed rows so the form ships predictable structure for the support reviewer; the
    // backend drops any row left blank.
    recentCharges: [
      { currencyCode: 'INR', amount: '', justification: '' },
      { currencyCode: 'INR', amount: '', justification: '' },
      { currencyCode: 'INR', amount: '', justification: '' },
    ],
    resumeFileId: '',
    resumeFileName: '',
    // Phase 14y — additional applicant-only fields imported from the original mentor
    // profile flow + bank-account details for the support reviewer + agreement
    // acceptances. All persisted on ApplicantDetails (NOT Practitioner) until approval.
    dateOfBirth: '',
    genderId: '',
    languages: [],
    longBio: '',
    websiteUrl: '',
    location: { address: '', city: '', countryCode: '', pincode: '', timeZoneId: '' },
    banking: {
      accountHolderName: '',
      accountNumber: '',
      ifsc: '',
      bankName: '',
      accountType: '',
      upiId: '',
      notes: '',
    },
    agreementsAccepted: [],
  })
  // Phase 14y — list of agreements pulled from /api/secure/teacher/user-agreement/list
  // once the JWT is available. Kept separate from `form` because it's read-only catalog
  // metadata; what the applicant ticks goes into form.agreementsAccepted.
  const [agreementCatalog, setAgreementCatalog] = useState([])

  // Authenticated GET on mount — hydrates the form from whatever Stage 1 already saved so the
  // applicant doesn't lose their bio / qualifications when they land on this screen.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/secure/practitioner-self/enrichment`, {
          headers: { Authorization: `Bearer ${authToken}` },
        })
        if (cancelled) return
        const data = res?.data?.data || res?.data || {}
        setHydrated(data)
        // Pad recentCharges to exactly 3 rows so the agent always sees three input slots.
        const padCharges = (arr) => {
          const base = Array.isArray(arr)
            ? arr.map((r) => ({
                currencyCode: r?.currencyCode || 'INR',
                amount: r?.amount ?? '',
                justification: r?.justification || '',
              }))
            : []
          while (base.length < 3) {
            base.push({ currencyCode: 'INR', amount: '', justification: '' })
          }
          return base.slice(0, 3)
        }
        setForm({
          practitionerSlug: data.practitionerSlug || summary?.practitionerSlug || '',
          shortBio: data.shortBio || '',
          qualifications: Array.isArray(data.qualifications) ? data.qualifications : [],
          specializations: Array.isArray(data.specializations) ? data.specializations : [],
          registrationIds: Array.isArray(data.registrationIds)
            ? data.registrationIds.map((r) => ({ typeId: r.typeId || '', id: r.id || '' }))
            : [],
          profilePicId: data.profilePicId || '',
          mandatoryCredentialDocFileId: data.mandatoryCredentialDocFileId || '',
          mandatoryCredentialDocTitle: data.mandatoryCredentialDocTitle || '',
          // Stage-1 LinkedIn wins on first hydration so we don't blow away what the applicant
          // typed before submit; otherwise prefer what the server already has on file.
          linkedinProfileUrl:
            data.linkedinProfileUrl || pendingLinkedin || '',
          hasOtherAffiliations: Boolean(data.hasOtherAffiliations),
          currentAffiliations: Array.isArray(data.currentAffiliations)
            ? data.currentAffiliations.map((a) => ({
                name: a?.name || '',
                url: a?.url || '',
                notes: a?.notes || '',
              }))
            : [],
          recentCharges: padCharges(data.recentCharges),
          resumeFileId: data.resumeFileId || '',
          resumeFileName: data.resumeFileName || '',
          // Phase 14y additions — hydrate the new applicant-only fields.
          dateOfBirth: data.dateOfBirth || '',
          genderId: data.genderId || '',
          languages: Array.isArray(data.languages) ? data.languages : [],
          longBio: data.longBio || '',
          websiteUrl: data.websiteUrl || '',
          location: {
            address: data.location?.address || '',
            city: data.location?.city || '',
            countryCode: data.location?.countryCode || '',
            pincode: data.location?.pincode || '',
            timeZoneId:
              data.location?.timeZoneId ||
              (typeof Intl !== 'undefined'
                ? Intl.DateTimeFormat().resolvedOptions().timeZone
                : ''),
          },
          banking: {
            accountHolderName: data.banking?.accountHolderName || '',
            accountNumber: data.banking?.accountNumber || '',
            ifsc: data.banking?.ifsc || '',
            bankName: data.banking?.bankName || '',
            accountType: data.banking?.accountType || '',
            upiId: data.banking?.upiId || '',
            notes: data.banking?.notes || '',
          },
          agreementsAccepted: Array.isArray(data.agreementsAccepted)
            ? data.agreementsAccepted.map((a) => ({
                agreementId: a.agreementId || '',
                title: a.title || '',
                version: a.version || '',
                acceptedIp: a.acceptedIp || '',
                acceptedLatLng: a.acceptedLatLng || '',
                acceptedUserAgent: a.acceptedUserAgent || '',
              }))
            : [],
        })
        // Phase 14z — hydrate the signature + checkbox state from previously-saved
        // ApplicantDetails so the applicant doesn't have to re-tick every time they
        // come back via /apply/resume.
        if (data.signatureMediaId) setSignatureMediaId(data.signatureMediaId)
        const accepted = Array.isArray(data.agreementsAccepted)
          ? data.agreementsAccepted
          : []
        if (accepted.some((a) => a.agreementId === TERMS_AGREEMENT_ID)) {
          setAcceptedTerms(true)
        }
        if (accepted.some((a) => a.agreementId === PRIVACY_AGREEMENT_ID)) {
          setAcceptedPrivacy(true)
        }
      } catch {
        // Hydration failure is non-fatal — the applicant just starts from a blank form.
      } finally {
        if (!cancelled) setHydrating(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [authToken])

  // Phase 14y — fetch the agreement catalog scoped to this applicant. Endpoint accepts
  // ROLE_TEACHER and ROLE_CREATOR; the applicant's JWT carries ROLE_CREATOR so this works.
  // Failure is non-fatal — the agreement section just renders empty if we can't load it.
  useEffect(() => {
    if (!authToken) return
    let cancelled = false
    ;(async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/secure/teacher/user-agreement/list`,
          { headers: { Authorization: `Bearer ${authToken}` } },
        )
        if (cancelled) return
        const list = Array.isArray(res?.data) ? res.data : res?.data?.data || []
        setAgreementCatalog(Array.isArray(list) ? list : [])
      } catch {
        if (!cancelled) setAgreementCatalog([])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [authToken])

  // Phase 14z — bridge the in-memory auth token to localStorage for the lifetime of this
  // form so PictureUploadComponent / SignaturePad / DocumentUploadComponent can call
  // /api/media/file/upload via the platform's standard postDataWithoutParams helper (which
  // reads the bearer from localStorage). We restore whatever was there on unmount so we
  // don't leak the token any longer than the editor needs it. Pairs with the Phase 14t
  // decision to keep the apply token off localStorage by default.
  //
  // Phase 14e — when the applicant has just hit "Submit for review" we explicitly *want*
  // the token to outlive the form so ProtectedRoute lets the redirect into
  // /mentor-home/kyc-pending-actions through. The cleanup checks the
  // `practitionerApplicationJustSubmitted` sessionStorage flag set by the submit handler
  // and skips the restore in that case.
  useEffect(() => {
    if (!authToken || typeof window === 'undefined') return
    const prev = window.localStorage.getItem('token')
    window.localStorage.setItem('token', authToken)
    return () => {
      if (window.sessionStorage.getItem('practitionerApplicationJustSubmitted') === '1') {
        return
      }
      if (prev) window.localStorage.setItem('token', prev)
      else window.localStorage.removeItem('token')
    }
  }, [authToken])

  // Phase 14z — pre-fetch audit context (IP + lat/lng + UA) on mount so it's ready when the
  // applicant ticks consent boxes. We don't gate the form on this — every helper call is
  // best-effort.
  useEffect(() => {
    let cancelled = false
    captureAcceptanceAuditContext().then((ctx) => {
      if (!cancelled) auditContextRef.current = ctx
    })
    return () => {
      cancelled = true
    }
  }, [])

  // Phase 14z — keep the agreementsAccepted list in sync with the ToS / Privacy checkboxes
  // and the in-memory audit context. This is the single source-of-truth that gets shipped
  // up to the server on every save.
  useEffect(() => {
    setForm((p) => {
      const others = (p.agreementsAccepted || []).filter(
        (a) => a.agreementId !== TERMS_AGREEMENT_ID && a.agreementId !== PRIVACY_AGREEMENT_ID,
      )
      const next = [...others]
      const ctx = auditContextRef.current || {}
      if (acceptedTerms) {
        next.push({
          agreementId: TERMS_AGREEMENT_ID,
          title: 'AUMHUM Terms of Service',
          version: '2025-04',
          acceptedIp: ctx.ip || '',
          acceptedLatLng: ctx.latLng || '',
          acceptedUserAgent: ctx.userAgent || '',
        })
      }
      if (acceptedPrivacy) {
        next.push({
          agreementId: PRIVACY_AGREEMENT_ID,
          title: 'AUMHUM Privacy Policy',
          version: '2025-04',
          acceptedIp: ctx.ip || '',
          acceptedLatLng: ctx.latLng || '',
          acceptedUserAgent: ctx.userAgent || '',
        })
      }
      return { ...p, agreementsAccepted: next }
    })
  }, [acceptedTerms, acceptedPrivacy])

  async function save({ markReady = false } = {}) {
    if (markReady) {
      // Phase 14z — gate Submit-for-review on the new mandatory fields. Save-progress
      // stays unrestricted so the applicant can save partial work mid-form.
      if (!signatureMediaId) {
        toast.error('Please add your signature before submitting.')
        return
      }
      if (!acceptedTerms || !acceptedPrivacy) {
        toast.error('Please accept the Terms of Service and Privacy Policy before submitting.')
        return
      }
    }
    // Phase 14z — refresh audit context just before submit so the timestamps + IP + lat/lng
    // recorded against ToS / Privacy match the actual moment of consent (not the time the
    // form mounted). The two checkbox effects then re-emit the entries with the latest ctx.
    if (markReady) {
      try {
        auditContextRef.current = await captureAcceptanceAuditContext()
      } catch {
        /* non-fatal */
      }
    }
    setSaving(true)
    try {
      const payload = {
        slug: form.practitionerSlug || '',
        shortBio: form.shortBio,
        qualifications: form.qualifications,
        specializations: form.specializations,
        registrationIds: form.registrationIds.filter(
          (r) => r.typeId?.trim() && r.id?.trim(),
        ),
        profilePicId: form.profilePicId || undefined,
        mandatoryCredentialDocFileId: form.mandatoryCredentialDocFileId || undefined,
        mandatoryCredentialDocTitle: form.mandatoryCredentialDocTitle || undefined,
        signatureMediaId: signatureMediaId || undefined,
        // Phase 14u — applicant-only fields. Empty strings come through as "" rather than
        // undefined so the server can clear a previously-saved value when the applicant
        // explicitly blanked the input; recentCharges entries with no amount + no
        // justification are dropped server-side.
        linkedinProfileUrl: form.linkedinProfileUrl || '',
        hasOtherAffiliations: Boolean(form.hasOtherAffiliations),
        currentAffiliations: (form.currentAffiliations || [])
          .map((a) => ({
            name: a?.name?.trim() || '',
            url: a?.url?.trim() || '',
            notes: a?.notes?.trim() || '',
          }))
          .filter((a) => a.name || a.url || a.notes),
        recentCharges: (form.recentCharges || []).map((c) => ({
          currencyCode: c?.currencyCode || 'INR',
          amount:
            c?.amount === '' || c?.amount == null ? null : Number(c.amount),
          justification: c?.justification?.trim() || '',
        })),
        resumeFileId: form.resumeFileId || '',
        resumeFileName: form.resumeFileName || '',
        // Phase 14y — applicant profile + banking + agreements. Empty strings come through
        // as "" rather than undefined so the server can clear a field the applicant
        // explicitly blanked since the last save.
        dateOfBirth: form.dateOfBirth || '',
        genderId: form.genderId || '',
        languages: form.languages || [],
        longBio: form.longBio || '',
        websiteUrl: form.websiteUrl || '',
        location: {
          address: form.location?.address || '',
          city: form.location?.city || '',
          countryCode: form.location?.countryCode || '',
          pincode: form.location?.pincode || '',
          timeZoneId: form.location?.timeZoneId || '',
        },
        banking: {
          accountHolderName: form.banking?.accountHolderName || '',
          accountNumber: form.banking?.accountNumber || '',
          ifsc: form.banking?.ifsc || '',
          bankName: form.banking?.bankName || '',
          accountType: form.banking?.accountType || '',
          upiId: form.banking?.upiId || '',
          notes: form.banking?.notes || '',
        },
        agreementsAccepted: (form.agreementsAccepted || []).map((a) => ({
          agreementId: a.agreementId || '',
          title: a.title || '',
          version: a.version || '',
          acceptedIp: a.acceptedIp || '',
          // Phase 14z — extended audit context.
          acceptedLatLng: a.acceptedLatLng || '',
          acceptedUserAgent: a.acceptedUserAgent || '',
        })),
        markReady,
      }
      await axios.post(
        `${API_BASE}/api/secure/practitioner-self/enrichment`,
        payload,
        { headers: { Authorization: `Bearer ${authToken}` } },
      )
      toast.success(markReady ? 'Submitted for review.' : 'Saved.')
      if (markReady) {
        // Phase 14e — final "Submit for review" hands the applicant off to the mentor-portal
        // pending-review gate page (/mentor-home/kyc-pending-actions). Persist the apply auth
        // token + authorities to localStorage durably (the bridge useEffect's cleanup would
        // otherwise tear them down on unmount), in the same shape as the platform's standard
        // sign-in flow so ProtectedRoute lets us through.
        try {
          if (typeof window !== 'undefined' && authToken) {
            window.localStorage.setItem('token', authToken)
            window.sessionStorage.setItem('token', authToken)
            const authoritiesArr =
              (Array.isArray(summary?.authorities) && summary.authorities) ||
              ['ROLE_TEACHER', 'ROLE_CREATOR', 'ROLE_USER']
            window.localStorage.setItem('authorities', JSON.stringify(authoritiesArr))
            window.sessionStorage.setItem('authorities', JSON.stringify(authoritiesArr))
            // Hint the mentor-portal layout that this user just submitted, so the gate page
            // surfaces the "Application submitted" framing + submission datetime + support
            // contacts instead of the generic "KYC pending" copy.
            window.sessionStorage.setItem('practitionerApplicationJustSubmitted', '1')
          }
        } catch (storageErr) {
          // Non-fatal — even without storage the user still sees the success toast.
        }
        setSubmitted(true)
        // Defer the hand-off so the toast and the inline success card flash before the
        // location swap (gives the user a confirmation visual + lets any pending media
        // uploads in flight complete).
        if (typeof window !== 'undefined') {
          window.setTimeout(() => {
            window.location.replace('/mentor-home/kyc-pending-actions')
          }, 1400)
        }
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Could not save.'
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  // File-upload helper that piggy-backs the bearer token onto the existing /api/secure/media
  // endpoint. We re-use the platform's standard upload route rather than minting an /apply-
  // specific one so the same media id flows through the rest of the system without aliasing.
  async function uploadFile(file) {
    if (!file) return null
    const formData = new FormData()
    formData.append('file', file)
    try {
      const res = await axios.post(
        `${API_BASE}/api/media/file/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'multipart/form-data',
          },
        },
      )
      const data = res?.data?.data || res?.data
      return data?.fileId || null
    } catch (err) {
      toast.error('Upload failed.')
      return null
    }
  }

  if (submitted) {
    return (
      <ApplyShell
        title="Submitted for review"
        subtitle="Thanks — we have everything we need for now. Our team will review your application and reach out with next steps over email or WhatsApp."
      >
        <Alert severity="success" sx={{ mb: 2 }}>
          Your application is in our queue. Reference id:{' '}
          <strong>{summary?.practitionerId}</strong>
        </Alert>
      </ApplyShell>
    )
  }

  if (hydrating) {
    return (
      <ApplyShell title="Loading your application…">
        <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </ApplyShell>
    )
  }

  // Phase 14z — applicant name + contact pulled from the hydrated record so the page can
  // show "you're applying as <Name>" front and centre. The fullName is read-only — there's
  // no edit affordance on this page; if it's wrong the support reviewer corrects it later.
  const applicantName = (hydrated?.fullName || '').trim()
  const applicantContact = hydrated?.email || hydrated?.mobile || ''
  // Phase 14ai — slug auto-derived server-side; no longer rendered in the enrichment form.
  const bioCategory = ''
  return (
    <ApplyShell
      title={
        applicantName
          ? `Welcome, ${applicantName.split(' ')[0]}`
          : 'A few more details'
      }
      subtitle={
        applicantName
          ? `Applying as ${applicantName}. You're signed in — fill what you can; everything is saved automatically as you go.`
          : "You're signed in. Filling these out now means our team has fewer follow-ups for you. You can come back to this tab to keep editing — your progress is saved."
      }
    >
      <Stack spacing={2}>
        {/* Phase 14z — modernised sign-in banner with the applicant's full name front-and-
            centre and the reference id chipped. The full name is uneditable here (the
            support reviewer can correct typos via the support-portal onboarding form). */}
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 2,
            background:
              'linear-gradient(135deg, rgba(16,185,129,0.06) 0%, rgba(124,58,237,0.04) 100%)',
            borderColor: 'rgba(16,185,129,0.3)',
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
          >
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircle sx={{ color: 'rgb(16, 185, 129)' }} fontSize="small" />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {applicantName ? `Signed in as ${applicantName}` : 'Signed in'}
                </Typography>
              </Stack>
              {applicantContact && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.25 }}
                >
                  {applicantContact}
                </Typography>
              )}
            </Box>
            <Chip
              size="small"
              variant="outlined"
              label={`Ref: ${summary?.practitionerId || ''}`}
              sx={{ fontFamily: 'monospace', fontSize: 11 }}
            />
          </Stack>
        </Paper>

        {/* Phase 14u — read-only summary of which discipline / practitioner type this form is
            for. Mirrors the locked chips on the Stage 1 invitation card so the applicant has
            a constant reminder of the path they're applying for. */}
        <Paper
          variant="outlined"
          sx={{ p: 1.5, borderRadius: 2, background: 'rgba(124,58,237,0.04)' }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 0.75, fontWeight: 600 }}
          >
            This application is for
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {practitionerType && (
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={`Type: ${getTypeLabel(practitionerType)}`}
              />
            )}
            {discipline && (
              <Chip
                size="small"
                color="info"
                variant="outlined"
                label={`Discipline: ${getDisciplineLabel(discipline, practitionerType)}`}
              />
            )}
            {role && (
              <Chip
                size="small"
                color="info"
                variant="outlined"
                label={`Role: ${getRoleLabel(role, practitionerType)}`}
              />
            )}
          </Stack>
        </Paper>

        {/* Phase 14z — profile photo. Switched from a plain <input type="file" /> to the
            shared PictureUploadComponent so the applicant gets the same Doka-backed
            crop / resize / rotate editor the rest of the platform uses. The "guidelines"
            link sits next to the title and opens a styled approved-vs-not-approved card
            so the applicant has a chance to self-correct before uploading. */}
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1 }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Profile photo
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Tooltip title="See what works (and what doesn't) for a profile photo">
                <IconButton
                  size="small"
                  onClick={() => setPhotoGuidelinesOpen(true)}
                  sx={{ color: '#7c3aed' }}
                >
                  <HelpOutline fontSize="small" />
                </IconButton>
              </Tooltip>
              <MuiLink
                component="button"
                type="button"
                onClick={() => setPhotoGuidelinesOpen(true)}
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#7c3aed',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Photo guidelines
              </MuiLink>
            </Stack>
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 1.5 }}
          >
            Solo portrait, clear face, plain background. You will get a crop / rotate /
            resize editor before the photo is saved.
          </Typography>
          <PictureUploadComponent
            id="profilePicId"
            value={form.profilePicId}
            onChange={(event) =>
              setForm((p) => ({ ...p, profilePicId: event?.target?.value || '' }))
            }
            aspectWidth={400}
            aspectHeight={400}
            showRemoveButton={Boolean(form.profilePicId)}
            hideRecommendedText
          />
        </Paper>

        <FieldSection
          title="Profile story"
          caption="Write the patient-facing version once, with examples tuned to this discipline and role."
          icon={Article}
          accent="bio"
        >
          <Stack spacing={1.5}>
            {/* Phase 14ai — slug is no longer applicant-editable on /apply. The server
                auto-generates a unique slug from the applicant's name on Stage-1 submit;
                support can still adjust it from the support-portal onboarding page later. */}
            <BioTextField
              label="Short bio"
              minRows={4}
              value={form.shortBio}
              onChangeValue={(shortBio) => setForm((p) => ({ ...p, shortBio }))}
              helperText="Use Tab while focused here to drop in the first suggested sentence."
              practitionerType={practitionerType}
              discipline={discipline}
              role={role}
              category={bioCategory}
            />
            <BioTextField
              label="Detailed bio (optional)"
              minRows={6}
              value={form.longBio}
              onChangeValue={(longBio) => setForm((p) => ({ ...p, longBio }))}
              helperText="Optional. Use Tab for a starter sentence, then expand with approach, populations served, and style of care."
              practitionerType={practitionerType}
              discipline={discipline}
              role={role}
              category={bioCategory}
            />
            <TextField
              label="Website (optional)"
              value={form.websiteUrl}
              onChange={(e) => setForm((p) => ({ ...p, websiteUrl: e.target.value }))}
              placeholder="https://example.com"
              helperText="Personal site, marketplace profile, or published profile patients can read."
            />
          </Stack>
        </FieldSection>

        {/* Phase 14y — personal details + location, mirroring the mentor profile screen. */}
        <FieldSection
          title="Personal details"
          caption="Small facts that help support complete your profile accurately."
          icon={Language}
          accent="contact"
        >
          <Stack spacing={1.5}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                label="Date of birth"
                type="date"
                fullWidth
                size="small"
                value={form.dateOfBirth}
                onChange={(e) =>
                  setForm((p) => ({ ...p, dateOfBirth: e.target.value }))
                }
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                select
                label="Gender"
                fullWidth
                size="small"
                value={form.genderId}
                onChange={(e) =>
                  setForm((p) => ({ ...p, genderId: e.target.value }))
                }
                SelectProps={{ native: true }}
              >
                <option value="">Prefer not to say</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="non-binary">Non-binary</option>
                <option value="other">Other</option>
              </TextField>
            </Stack>
            <Autocomplete
              multiple
              freeSolo
              options={[
                'en',
                'hi',
                'ta',
                'te',
                'kn',
                'ml',
                'mr',
                'gu',
                'bn',
                'pa',
                'or',
                'as',
                'ur',
                'es',
                'fr',
                'de',
                'ar',
              ]}
              value={form.languages}
              onChange={(_e, val) => setForm((p) => ({ ...p, languages: val }))}
              renderTags={(value, getTagProps) =>
                value.map((label, index) => (
                  <Chip
                    label={label}
                    size="small"
                    {...getTagProps({ index })}
                    key={`${label}-${index}`}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Languages you practise in"
                  helperText="ISO codes — en, hi, ta, kn, etc. Add as many as apply."
                />
              )}
            />
          </Stack>
        </FieldSection>

        {/* Phase 14y — location block. Light-touch version of the mentor profile address
            section: enough for the support reviewer to know where the practitioner sits
            geographically without overcomplicating Stage 1.5. */}
        <FieldSection
          title="Location"
          caption="Useful for time-zone, language, and geography-aware matching."
          icon={PersonPinCircle}
          accent="location"
        >
          <Stack spacing={1.25}>
            <TextField
              label="Address (optional)"
              size="small"
              value={form.location?.address || ''}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  location: { ...p.location, address: e.target.value },
                }))
              }
              fullWidth
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                label="City"
                size="small"
                fullWidth
                value={form.location?.city || ''}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    location: { ...p.location, city: e.target.value },
                  }))
                }
              />
              <TextField
                label="Country code"
                size="small"
                placeholder="IN"
                value={form.location?.countryCode || ''}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    location: {
                      ...p.location,
                      countryCode: e.target.value.toUpperCase(),
                    },
                  }))
                }
                sx={{ minWidth: 140 }}
                helperText="ISO-3166 alpha-2"
              />
              <TextField
                label="Pincode"
                size="small"
                value={form.location?.pincode || ''}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    location: { ...p.location, pincode: e.target.value },
                  }))
                }
                sx={{ minWidth: 140 }}
              />
            </Stack>
            <TextField
              label="Time zone"
              size="small"
              value={form.location?.timeZoneId || ''}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  location: { ...p.location, timeZoneId: e.target.value },
                }))
              }
              fullWidth
              helperText="IANA name, e.g. Asia/Kolkata. Auto-detected from your browser when you arrive here."
            />
          </Stack>
        </FieldSection>

        <FieldSection
          title="Qualifications and focus areas"
          caption="Pick from the catalog when available, or type your own clean labels."
          icon={WorkspacePremium}
          accent="credentials"
        >
          <Stack spacing={1.5}>
            {/* Qualifications */}
            {qualificationCatalog &&
        ((qualificationCatalog.preferred && qualificationCatalog.preferred.length > 0) ||
          (qualificationCatalog.fallback && qualificationCatalog.fallback.length > 0)) ? (
          (() => {
            const grouped = [
              ...(qualificationCatalog.preferred || []).map((item) => ({
                id: item.id || item.label,
                label: item.label,
                group:
                  'Suggested for ' +
                  (qualificationCatalog.displayLabel ||
                    (discipline || '').replace(/-/g, ' ')),
              })),
              ...(qualificationCatalog.fallback || []).map((item) => ({
                id: item.longTitle || item.title || item.id,
                label: item.longTitle || item.title || item.id,
                group: 'Other',
              })),
            ]
            return (
              <Autocomplete
                multiple
                freeSolo
                options={grouped}
                groupBy={(option) => option.group}
                getOptionLabel={(option) =>
                  typeof option === 'string'
                    ? option
                    : option?.label || option?.id || ''
                }
                isOptionEqualToValue={(option, value) =>
                  (typeof option === 'string' ? option : option.label) ===
                  (typeof value === 'string' ? value : value.label)
                }
                value={form.qualifications}
                onChange={(_e, nextValue) =>
                  setForm((p) => ({
                    ...p,
                    qualifications: nextValue
                      .map((v) => (typeof v === 'string' ? v : v.label))
                      .filter(Boolean),
                  }))
                }
                renderTags={(value, getTagProps) =>
                  value.map((label, index) => (
                    <Chip
                      label={label}
                      size="small"
                      {...getTagProps({ index })}
                      key={`${label}-${index}`}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Qualifications"
                    helperText={`Pick from the curated list for ${
                      qualificationCatalog.displayLabel ||
                      (discipline || '').replace(/-/g, ' ')
                    }, or type a custom value.`}
                  />
                )}
              />
            )
          })()
        ) : (
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={form.qualifications}
            onChange={(_e, val) => setForm((p) => ({ ...p, qualifications: val }))}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Qualifications"
                helperText="Type each qualification and press Enter (e.g. M.A. Clinical Psychology)."
              />
            )}
          />
            )}

            {/* Specializations */}
            {specializationCatalog &&
          ((specializationCatalog.preferred && specializationCatalog.preferred.length > 0) ||
            (specializationCatalog.fallback && specializationCatalog.fallback.length > 0)) && (
            (() => {
              const grouped = [
                ...(specializationCatalog.preferred || []).map((item) => ({
                  id: item.id || item.label,
                  label: item.label || item.title || item.id,
                  group:
                    'Suggested for ' +
                    (specializationCatalog.displayLabel ||
                      (discipline || '').replace(/-/g, ' ')),
                })),
                ...(specializationCatalog.fallback || []).map((item) => ({
                  id: item.id || item.title,
                  label: item.title || item.id,
                  group: 'Other',
                })),
              ]
              return (
                <Autocomplete
                  multiple
                  freeSolo
                  options={grouped}
                  groupBy={(option) => option.group}
                  getOptionLabel={(option) =>
                    typeof option === 'string'
                      ? option
                      : option?.label || option?.id || ''
                  }
                  isOptionEqualToValue={(option, value) =>
                    (typeof option === 'string' ? option : option.label) ===
                    (typeof value === 'string' ? value : value.label)
                  }
                  value={form.specializations}
                  onChange={(_e, nextValue) =>
                    setForm((p) => ({
                      ...p,
                      specializations: nextValue
                        .map((v) => (typeof v === 'string' ? v : v.label))
                        .filter(Boolean),
                    }))
                  }
                  renderTags={(value, getTagProps) =>
                    value.map((label, index) => (
                      <Chip
                        label={label}
                        size="small"
                        {...getTagProps({ index })}
                        key={`${label}-${index}`}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Specializations"
                      helperText={`Pick from the curated list for ${
                        specializationCatalog.displayLabel ||
                        (discipline || '').replace(/-/g, ' ')
                      }, or type a custom value.`}
                    />
                  )}
                />
              )
            })()
          )}
          </Stack>
        </FieldSection>

        <FieldSection
          title="Registration / license IDs"
          caption="Add RCI / MCI / IDA / etc. registration numbers if you hold them."
          icon={Badge}
          accent="credentials"
          action={
            <Button
              size="small"
              variant="outlined"
              onClick={() =>
                setForm((p) => ({
                  ...p,
                  registrationIds: [...p.registrationIds, { typeId: '', id: '' }],
                }))
              }
            >
              Add
            </Button>
          }
        >
          {form.registrationIds.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              No registration IDs added yet.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {form.registrationIds.map((reg, idx) => (
                <Stack
                  key={`reg-${idx}`}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems="center"
                >
                  <TextField
                    label="Type"
                    placeholder="RCI / MCI / IDA"
                    value={reg.typeId || ''}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        registrationIds: p.registrationIds.map((r, i) =>
                          i === idx ? { ...r, typeId: e.target.value } : r,
                        ),
                      }))
                    }
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Number"
                    value={reg.id || ''}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        registrationIds: p.registrationIds.map((r, i) =>
                          i === idx ? { ...r, id: e.target.value } : r,
                        ),
                      }))
                    }
                    fullWidth
                    size="small"
                  />
                  <Button
                    size="small"
                    color="error"
                    onClick={() =>
                      setForm((p) => ({
                        ...p,
                        registrationIds: p.registrationIds.filter((_r, i) => i !== idx),
                      }))
                    }
                  >
                    Remove
                  </Button>
                </Stack>
              ))}
            </Stack>
          )}
        </FieldSection>

        <FieldSection
          title="Highest credential document"
          caption="Upload a clear copy of your highest relevant credential. PDF or image, both work."
          icon={Description}
          accent="credentials"
        >
          <Stack spacing={1.25}>
            <TextField
              label="Document title"
              size="small"
              value={form.mandatoryCredentialDocTitle}
              onChange={(e) =>
                setForm((p) => ({ ...p, mandatoryCredentialDocTitle: e.target.value }))
              }
              placeholder="e.g. M.Phil Clinical Psychology degree"
            />
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
              {form.mandatoryCredentialDocFileId && (
                <Chip
                  label="Document on file"
                  color="success"
                  variant="outlined"
                  onDelete={() =>
                    setForm((p) => ({ ...p, mandatoryCredentialDocFileId: '' }))
                  }
                />
              )}
              <Button variant="outlined" component="label" disabled={saving}>
                {form.mandatoryCredentialDocFileId ? 'Replace document' : 'Upload document'}
                <input
                  type="file"
                  accept="application/pdf,image/*"
                  hidden
                  onChange={async (e) => {
                    const file = e.target.files?.[0]
                    if (!file) return
                    const id = await uploadFile(file)
                    if (id) setForm((p) => ({ ...p, mandatoryCredentialDocFileId: id }))
                  }}
                />
              </Button>
            </Stack>
          </Stack>
        </FieldSection>

        <FieldSection
          title="LinkedIn"
          caption="The LinkedIn domain and /in/ path are fixed; edit only the final handle."
          icon={LinkIcon}
          accent="links"
        >
          <UrlSlugField
            label="LinkedIn profile URL"
            prefix={LINKEDIN_PROFILE_URL_PREFIX}
            value={form.linkedinProfileUrl}
            onChange={(linkedinProfileUrl) =>
              setForm((p) => ({ ...p, linkedinProfileUrl }))
            }
            placeholder="ananya-rao"
            helperText="Paste a full LinkedIn URL or type only your /in/ slug."
            normalizeSlug={sanitizeLinkedinSlug}
            storeAsUrl
            trailingSlash
          />
        </FieldSection>

        <FieldSection
          title="Other practitioner platforms or services"
          caption="Tell us if you currently provide services through another platform, clinic, or employer."
          icon={Work}
          accent="links"
        >
          <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
            <Button
              size="small"
              variant={form.hasOtherAffiliations ? 'contained' : 'outlined'}
              onClick={() => setForm((p) => ({ ...p, hasOtherAffiliations: true }))}
            >
              Yes
            </Button>
            <Button
              size="small"
              variant={!form.hasOtherAffiliations ? 'contained' : 'outlined'}
              onClick={() =>
                setForm((p) => ({
                  ...p,
                  hasOtherAffiliations: false,
                  currentAffiliations: [],
                }))
              }
            >
              No
            </Button>
          </Stack>
          {form.hasOtherAffiliations && (
            <Stack spacing={1.25}>
              {(form.currentAffiliations.length === 0
                ? [{ name: '', url: '', notes: '' }]
                : form.currentAffiliations
              ).map((aff, idx) => (
                <Stack key={`aff-${idx}`} spacing={1}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <TextField
                      label="Service / platform name"
                      size="small"
                      fullWidth
                      value={aff.name || ''}
                      onChange={(e) =>
                        setForm((p) => {
                          const list =
                            p.currentAffiliations.length === 0
                              ? [{ name: '', url: '', notes: '' }]
                              : [...p.currentAffiliations]
                          list[idx] = { ...list[idx], name: e.target.value }
                          return { ...p, currentAffiliations: list }
                        })
                      }
                    />
                    <TextField
                      label="URL (optional)"
                      size="small"
                      fullWidth
                      value={aff.url || ''}
                      onChange={(e) =>
                        setForm((p) => {
                          const list =
                            p.currentAffiliations.length === 0
                              ? [{ name: '', url: '', notes: '' }]
                              : [...p.currentAffiliations]
                          list[idx] = { ...list[idx], url: e.target.value }
                          return { ...p, currentAffiliations: list }
                        })
                      }
                    />
                  </Stack>
                  <TextField
                    label="Notes (role, scope, frequency)"
                    size="small"
                    fullWidth
                    value={aff.notes || ''}
                    onChange={(e) =>
                      setForm((p) => {
                        const list =
                          p.currentAffiliations.length === 0
                            ? [{ name: '', url: '', notes: '' }]
                            : [...p.currentAffiliations]
                        list[idx] = { ...list[idx], notes: e.target.value }
                        return { ...p, currentAffiliations: list }
                      })
                    }
                  />
                  {form.currentAffiliations.length > 1 && (
                    <Stack direction="row" justifyContent="flex-end">
                      <Button
                        size="small"
                        color="error"
                        onClick={() =>
                          setForm((p) => ({
                            ...p,
                            currentAffiliations: p.currentAffiliations.filter(
                              (_x, i) => i !== idx,
                            ),
                          }))
                        }
                      >
                        Remove
                      </Button>
                    </Stack>
                  )}
                </Stack>
              ))}
              <Stack direction="row" justifyContent="flex-end">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    setForm((p) => ({
                      ...p,
                      currentAffiliations: [
                        ...(p.currentAffiliations.length === 0
                          ? [{ name: '', url: '', notes: '' }]
                          : p.currentAffiliations),
                        { name: '', url: '', notes: '' },
                      ],
                    }))
                  }
                >
                  Add another
                </Button>
              </Stack>
            </Stack>
          )}
        </FieldSection>

        <FieldSection
          title="Recent session fees"
          caption="Most recent first. Share the amount and a one-line reason for each."
          icon={Payments}
          accent="money"
        >
          <Stack spacing={1.5}>
            {form.recentCharges.map((c, idx) => (
              <Stack key={`charge-${idx}`} spacing={1}>
                <Typography variant="caption" color="text.secondary">
                  {idx === 0 ? 'Most recent patient' : idx === 1 ? 'Second most recent' : 'Third most recent'}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <TextField
                    label="Currency"
                    size="small"
                    sx={{ minWidth: 90 }}
                    value={c.currencyCode || 'INR'}
                    onChange={(e) =>
                      setForm((p) => {
                        const list = [...p.recentCharges]
                        list[idx] = { ...list[idx], currencyCode: e.target.value.toUpperCase() }
                        return { ...p, recentCharges: list }
                      })
                    }
                  />
                  <TextField
                    label="Amount"
                    size="small"
                    type="number"
                    fullWidth
                    value={c.amount ?? ''}
                    onChange={(e) =>
                      setForm((p) => {
                        const list = [...p.recentCharges]
                        list[idx] = { ...list[idx], amount: e.target.value }
                        return { ...p, recentCharges: list }
                      })
                    }
                  />
                </Stack>
                <TextField
                  label="Why this fee?"
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  value={c.justification || ''}
                  onChange={(e) =>
                    setForm((p) => {
                      const list = [...p.recentCharges]
                      list[idx] = { ...list[idx], justification: e.target.value }
                      return { ...p, recentCharges: list }
                    })
                  }
                />
              </Stack>
            ))}
          </Stack>
        </FieldSection>

        <FieldSection
          title="Resume / CV"
          caption="PDF or image. Our team uses this alongside your registration documents."
          icon={Description}
          accent="credentials"
        >
          <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
            {form.resumeFileId && (
              <Chip
                label={form.resumeFileName || 'Resume on file'}
                color="success"
                variant="outlined"
                onDelete={() =>
                  setForm((p) => ({ ...p, resumeFileId: '', resumeFileName: '' }))
                }
              />
            )}
            <Button variant="outlined" component="label" disabled={saving}>
              {form.resumeFileId ? 'Replace resume' : 'Upload resume'}
              <input
                type="file"
                accept="application/pdf,image/*"
                hidden
                onChange={async (e) => {
                  const file = e.target.files?.[0]
                  if (!file) return
                  const id = await uploadFile(file)
                  if (id) {
                    setForm((p) => ({
                      ...p,
                      resumeFileId: id,
                      resumeFileName: file.name || 'Resume',
                    }))
                  }
                }}
              />
            </Button>
          </Stack>
        </FieldSection>

        <FieldSection
          title="Bank account for payouts"
          caption="Used only to set up payouts after approval; support can correct it later."
          icon={AccountBalance}
          accent="money"
        >
          <Stack spacing={1.25}>
            <TextField
              label="Account holder name"
              size="small"
              fullWidth
              value={form.banking?.accountHolderName || ''}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  banking: { ...p.banking, accountHolderName: e.target.value },
                }))
              }
              helperText="Exactly as printed on your passbook / chequebook."
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                label="Account number"
                size="small"
                fullWidth
                value={form.banking?.accountNumber || ''}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    banking: { ...p.banking, accountNumber: e.target.value },
                  }))
                }
              />
              <TextField
                label="IFSC"
                size="small"
                value={form.banking?.ifsc || ''}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    banking: {
                      ...p.banking,
                      ifsc: e.target.value.toUpperCase(),
                    },
                  }))
                }
                placeholder="HDFC0001234"
                sx={{ minWidth: 160 }}
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                label="Bank name"
                size="small"
                fullWidth
                value={form.banking?.bankName || ''}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    banking: { ...p.banking, bankName: e.target.value },
                  }))
                }
              />
              <TextField
                select
                label="Account type"
                size="small"
                value={form.banking?.accountType || ''}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    banking: { ...p.banking, accountType: e.target.value },
                  }))
                }
                SelectProps={{ native: true }}
                sx={{ minWidth: 140 }}
              >
                <option value="">—</option>
                <option value="savings">Savings</option>
                <option value="current">Current</option>
                <option value="other">Other</option>
              </TextField>
            </Stack>
            <TextField
              label="UPI handle (optional)"
              size="small"
              fullWidth
              value={form.banking?.upiId || ''}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  banking: { ...p.banking, upiId: e.target.value },
                }))
              }
              placeholder="anwei@hdfcbank"
            />
            <TextField
              label="Notes (optional)"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={form.banking?.notes || ''}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  banking: { ...p.banking, notes: e.target.value },
                }))
              }
              placeholder="OD account, joint with spouse, SWIFT for international transfers, etc."
            />
          </Stack>
        </FieldSection>

        {/* Phase 14z — signature canvas. Required before "Submit for review" but not for
            "Save progress". The applicant can either draw with mouse/trackpad or upload a
            scanned image; the SignaturePad component already handles both modes. We store
            the resulting media id in TeacherPersonalize.signatureMediaId via the standard
            enrichment endpoint. */}
        <Paper
          variant="outlined"
          sx={{
            p: 2.5,
            borderRadius: 2,
            borderColor: signatureMediaId ? 'success.main' : 'rgba(124,58,237,0.4)',
            background: signatureMediaId
              ? 'rgba(16,185,129,0.03)'
              : 'rgba(124,58,237,0.03)',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1 }}
          >
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Draw sx={{ color: '#7c3aed', fontSize: 20 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Signature
                {!signatureMediaId && (
                  <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>
                    *
                  </Box>
                )}
              </Typography>
            </Stack>
            {signatureMediaId && (
              <Chip
                size="small"
                color="success"
                icon={<CheckCircle sx={{ fontSize: 16 }} />}
                label="Signature on file"
              />
            )}
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 1.5 }}
          >
            Sign here using your mouse, trackpad, or touchscreen — or upload a scanned
            image of your signature. This signature is used on prescriptions and reports
            you generate after approval.
          </Typography>
          <SignaturePad
            id="applicantSignatureMediaId"
            value={signatureMediaId}
            onChange={(event) => setSignatureMediaId(event?.target?.value || '')}
            mode="both"
            title="Your signature"
            helperText="Draw or upload — both work. Saved with the rest of your application."
          />
        </Paper>

        {/* Phase 14z — mandatory ToS + Privacy Policy checkboxes. Each tick captures the
            applicant's IP / approximate geolocation / user-agent (collected once on mount,
            refreshed at submit time) and lands on ApplicantDetails.agreementsAccepted as a
            durable consent record. The "Submit for review" button blocks until both are
            ticked plus the signature is on file (see save() guard above). */}
        <Paper
          variant="outlined"
          sx={{
            p: 2.5,
            borderRadius: 2,
            background:
              acceptedTerms && acceptedPrivacy
                ? 'rgba(16,185,129,0.03)'
                : 'rgba(124,58,237,0.03)',
            borderColor:
              acceptedTerms && acceptedPrivacy ? 'success.main' : 'rgba(124,58,237,0.4)',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <VerifiedUser sx={{ color: '#7c3aed', fontSize: 22 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Consent & policies
            </Typography>
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 1.5 }}
          >
            We record your acceptance time, IP, approximate location, and browser as an
            audit trail. Both boxes are required before submission.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                sx={{ color: '#7c3aed' }}
              />
            }
            label={
              <Typography variant="body2">
                I have read and agree to the{' '}
                <MuiLink
                  component="button"
                  type="button"
                  onClick={(e) => {
                    e.preventDefault()
                    setLegalDoc({ url: APPLY_TERMS_URL, title: 'Terms of Service' })
                  }}
                  sx={{
                    fontWeight: 600,
                    color: '#7c3aed',
                    verticalAlign: 'baseline',
                    textDecoration: 'underline',
                  }}
                >
                  Terms of Service
                </MuiLink>
                .
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={acceptedPrivacy}
                onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                sx={{ color: '#7c3aed' }}
              />
            }
            label={
              <Typography variant="body2">
                I have read and agree to the{' '}
                <MuiLink
                  component="button"
                  type="button"
                  onClick={(e) => {
                    e.preventDefault()
                    setLegalDoc({
                      url: APPLY_PRIVACY_POLICY_URL,
                      title: 'Privacy Policy',
                    })
                  }}
                  sx={{
                    fontWeight: 600,
                    color: '#7c3aed',
                    verticalAlign: 'baseline',
                    textDecoration: 'underline',
                  }}
                >
                  Privacy Policy
                </MuiLink>
                .
              </Typography>
            }
          />
          {/* Phase 14y — additional agreements (when the support team adds e.g. a code-of-
              conduct or instructor-terms entry to the user-agreement catalog) keep using
              the existing /api/secure/teacher/user-agreement/list flow but render below the
              two mandatory ToS / Privacy boxes so they don't crowd the consent decision. */}
          {agreementCatalog && agreementCatalog.length > 0 && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 1, fontWeight: 600 }}
              >
                Additional agreements
              </Typography>
              <Stack spacing={1}>
                {agreementCatalog.map((agreement) => {
                  const id = agreement?.id || agreement?.agreementId
                  if (!id) return null
                  // Skip ToS / Privacy entries already covered above.
                  if (id === TERMS_AGREEMENT_ID || id === PRIVACY_AGREEMENT_ID) return null
                  const accepted = (form.agreementsAccepted || []).some(
                    (a) => a.agreementId === id,
                  )
                  return (
                    <Box
                      key={`agreement-${id}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        p: 1,
                        borderRadius: 1.5,
                        border: '1px solid',
                        borderColor: accepted ? 'success.main' : 'divider',
                        background: accepted ? 'rgba(16,185,129,0.04)' : 'transparent',
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={accepted}
                        onChange={(e) => {
                          const checked = e.target.checked
                          const ctx = auditContextRef.current || {}
                          setForm((p) => {
                            const list = (p.agreementsAccepted || []).filter(
                              (a) => a.agreementId !== id,
                            )
                            if (checked) {
                              list.push({
                                agreementId: id,
                                title: agreement?.title || '',
                                version: agreement?.version || '',
                                acceptedIp: ctx.ip || '',
                                acceptedLatLng: ctx.latLng || '',
                                acceptedUserAgent: ctx.userAgent || '',
                              })
                            }
                            return { ...p, agreementsAccepted: list }
                          })
                        }}
                        sx={{ mt: -0.25, color: '#7c3aed' }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {agreement?.title || id}
                        </Typography>
                        {agreement?.shortDesc && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            {agreement.shortDesc}
                          </Typography>
                        )}
                        <MuiLink
                          component="button"
                          type="button"
                          onClick={(e) => {
                            e.preventDefault()
                            setLegalDoc({
                              url: `/mentor-home/agreements/${id}`,
                              title: agreement?.title || 'Agreement',
                            })
                          }}
                          sx={{
                            fontSize: '0.75rem',
                            color: '#7c3aed',
                            textDecoration: 'none',
                            fontWeight: 500,
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          Read full agreement →
                        </MuiLink>
                      </Box>
                    </Box>
                  )
                })}
              </Stack>
            </>
          )}
        </Paper>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          justifyContent="flex-end"
          sx={{ mt: 2 }}
        >
          <Button onClick={() => save({ markReady: false })} disabled={saving}>
            {saving ? 'Saving…' : 'Save progress'}
          </Button>
          <Button
            variant="contained"
            onClick={() => save({ markReady: true })}
            disabled={
              saving || !signatureMediaId || !acceptedTerms || !acceptedPrivacy
            }
            sx={{
              background:
                signatureMediaId && acceptedTerms && acceptedPrivacy
                  ? 'linear-gradient(135deg, #7c3aed 0%, #ec8074 100%)'
                  : undefined,
            }}
          >
            {saving ? 'Submitting…' : 'Submit for review'}
          </Button>
        </Stack>
        <ProfilePhotoGuidelinesDialog
          open={photoGuidelinesOpen}
          onClose={() => setPhotoGuidelinesOpen(false)}
        />
        <LegalDocDrawer
          open={Boolean(legalDoc)}
          onClose={() => setLegalDoc(null)}
          url={legalDoc?.url || ''}
          title={legalDoc?.title || ''}
        />
      </Stack>
    </ApplyShell>
  )
}

function ApplyTypePage() {
  const { type } = useParams()
  const navigate = useNavigate()
  const tile = TYPE_TILES.find((t) => t.id === type)
  const internalType =
    type === 'doctor' || type === 'psychology-student' ? type : null

  if (!internalType) {
    return (
      <ApplyShell title="Not found">
        <Alert severity="warning">
          This application path does not exist. <a href="/apply">Pick from the list</a>.
        </Alert>
      </ApplyShell>
    )
  }

  return (
    <ApplyShell
      title={`${tile?.label || 'Application'}`}
      subtitle="Tell us about yourself. We'll review and reach out with next steps."
      onBack={() => navigate('/apply')}
    >
      <ApplyMeta pageKind="organic-type" type={internalType} />
      <ApplicationForm
        mode="basic"
        practitionerType={internalType}
        onCancel={() => navigate('/apply')}
      />
    </ApplyShell>
  )
}

function ApplyMhpRolePage() {
  const { role } = useParams()
  const navigate = useNavigate()
  const tile = MHP_ROLE_TILES.find((t) => t.id === role)
  if (!tile) {
    return (
      <ApplyShell title="Not found">
        <Alert severity="warning">
          This role does not exist. <a href="/apply/mhp">Pick a role</a>.
        </Alert>
      </ApplyShell>
    )
  }
  return (
    <ApplyShell
      title={`Apply as ${tile.label}`}
      subtitle="A short application, then our team reviews and reaches out."
      onBack={() => navigate('/apply/mhp')}
    >
      <ApplyMeta
        pageKind="organic-mhp-role"
        type="therapist"
        discipline={tile.discipline}
        role={tile.id}
      />
      <ApplicationForm
        mode="basic"
        practitionerType="therapist"
        discipline={tile.discipline}
        role={tile.id}
        subCategoryOptions={SUBCATEGORY_OPTIONS[tile.id]}
        onCancel={() => navigate('/apply/mhp')}
      />
    </ApplyShell>
  )
}

function ApplyInvitationPage() {
  const { token } = useParams()
  const navigate = useNavigate()
  const [state, setState] = useState({
    loading: true,
    error: '',
    invitation: null,
    invitationStatus: '',
    resumeHint: null,
  })

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const recaptchaToken = await fetchRecaptchaToken('apply_invitation_validate')
        const params = recaptchaToken ? { recaptchaToken } : {}
        const res = await applyAnonAxios.get(
          // /api/nc/ — see comment on PUBLIC_BASE above.
          `/api/nc/practitioner-invitations/${token}/validate`,
          { params },
        )
        if (cancelled) return
        const payload = res?.data?.data || res?.data || {}
        const data = payload?.invitation
        const invitationStatus = payload?.invitationStatus || data?.status || ''
        if (invitationStatus === 'consumed') {
          setState({
            loading: false,
            error: '',
            invitation: null,
            invitationStatus,
            resumeHint: normalizeResumeHint(payload?.resumeHint || data?.resumeHint, data),
          })
        } else if (!data) {
          setState({
            loading: false,
            error: 'Invitation could not be validated.',
            invitation: null,
            invitationStatus: '',
            resumeHint: null,
          })
        } else {
          setState({
            loading: false,
            error: '',
            invitation: data,
            invitationStatus,
            resumeHint: null,
          })
        }
      } catch (err) {
        if (cancelled) return
        const payload = err?.response?.data?.data || err?.response?.data || {}
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          'Invitation invalid or expired.'
        const invitationStatus = payload?.invitationStatus || payload?.invitation?.status || ''
        setState({
          loading: false,
          error: msg,
          invitation: null,
          invitationStatus,
          resumeHint: normalizeResumeHint(payload?.resumeHint || payload?.invitation?.resumeHint, payload?.invitation),
        })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [token])

  if (state.loading) {
    return (
      <ApplyShell title="Validating invitation…">
        <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </ApplyShell>
    )
  }

  if (state.invitationStatus === 'consumed') {
    const resumeHint = normalizeResumeHint(state.resumeHint)
    return (
      <ApplyShell
        title="Your application is already started"
        subtitle="This invitation link has already been used. The next step is to sign in and complete the remaining profile details."
      >
        <Stack spacing={2}>
          <Alert
            severity="info"
            icon={<VerifiedUser />}
            sx={{
              borderRadius: 3,
              '& .MuiAlert-message': { width: '100%' },
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>
              Sign in is required to continue
            </Typography>
            <Typography variant="body2">
              We have saved the application created from this invitation. Please sign in to
              complete your bio, qualifications, recent fees, resume and bank details.
            </Typography>
          </Alert>

          <Paper
            variant="outlined"
            sx={{
              p: { xs: 2, sm: 2.5 },
              borderRadius: 3,
              borderColor: 'rgba(124,58,237,0.22)',
              background:
                'linear-gradient(135deg, rgba(124,58,237,0.08), rgba(15,118,110,0.06))',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: 'rgba(124,58,237,0.12)',
                    color: '#6d28d9',
                  }}
                >
                  <AlternateEmail fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    Continue with your account
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    {resumeHint.maskedContact
                      ? `${resumeHint.channelLabel} ${resumeHint.maskedContact}`
                      : 'Use the contact used for this application'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    A one-time sign-in code will be sent after you enter the full contact.
                  </Typography>
                </Box>
              </Stack>
              <Button
                variant="contained"
                size="large"
                startIcon={<CheckCircle />}
                onClick={() => navigate(buildResumeUrl(resumeHint))}
                sx={{ alignSelf: { xs: 'stretch', sm: 'center' } }}
              >
                Sign in to continue
              </Button>
            </Stack>
          </Paper>
        </Stack>
      </ApplyShell>
    )
  }

  if (state.error || !state.invitation) {
    // Phase 14w — heuristically detect a "consumed" invitation so we can show a clearer
    // message + a path back into the enrichment form. The validate endpoint returns the
    // string in ResponseException.message; we string-match because there's no machine-
    // readable code today. False positives just push the applicant to the resume page,
    // which is a better fallback than the bare "Invitation invalid" wall.
    const msg = (state.error || '').toLowerCase()
    const isConsumed =
      msg.includes('consumed') || msg.includes('already') || msg.includes('used')
    if (isConsumed) {
      const resumeHint = normalizeResumeHint(state.resumeHint)
      return (
        <ApplyShell
          title="Your application is already started"
          subtitle="This invitation link has already been used. The next step is to sign in and complete the remaining profile details."
        >
          <Stack spacing={2}>
            <Alert severity="info" icon={<VerifiedUser />} sx={{ borderRadius: 3 }}>
              Your application was saved from this pre-generated invitation. Please sign in to
              finish the remaining steps; the original link cannot be used to submit again.
            </Alert>
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: 3,
                borderColor: 'rgba(124,58,237,0.22)',
                background:
                  'linear-gradient(135deg, rgba(124,58,237,0.08), rgba(15,118,110,0.06))',
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
              >
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    Continue with your account
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    {resumeHint.maskedContact
                      ? `${resumeHint.channelLabel} ${resumeHint.maskedContact}`
                      : 'Use the contact used for this application'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Complete your profile by signing in with a one-time code.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate(buildResumeUrl(resumeHint))}
                  sx={{ alignSelf: { xs: 'stretch', sm: 'center' } }}
                >
                  Sign in to continue
                </Button>
              </Stack>
            </Paper>
          </Stack>
        </ApplyShell>
      )
    }
    return (
      <ApplyShell title="Invitation invalid">
        <Alert severity="error">{state.error || 'Invitation not found.'}</Alert>
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => navigate('/apply/resume')}>
              I already applied — sign me in
            </Button>
            <Button variant="outlined" onClick={() => navigate('/apply')}>
              Apply via the public form instead
            </Button>
          </Stack>
        </Box>
      </ApplyShell>
    )
  }

  const inv = state.invitation
  const role = inv.role
  // Phase 14ad — name in title. The subtext keeps the welcome copy. The applicant name is
  // sanitized to first-name only so titles like "Welcome, Anwei Pan" stay readable on
  // narrow screens and email previews; the full name reappears later in the consent /
  // signed-in banner.
  const prefillName = (inv.prefillFullName || '').trim()
  const firstName = prefillName ? prefillName.split(/\s+/)[0] : ''
  return (
    <ApplyShell
      title={firstName ? `Welcome, ${firstName}` : 'Welcome — complete your application'}
      subtitle={
        prefillName
          ? `You've been invited as ${prefillName}. Fill in the remaining details below — we'll get you onboarded promptly.`
          : "You've been invited directly. Fill in your details below; we'll get you onboarded promptly."
      }
    >
      <ApplyMeta
        pageKind="invitation"
        type={inv.practitionerType}
        discipline={inv.discipline}
        role={role}
        subCategory={inv.subCategory}
        applicantName={inv.prefillFullName}
      />
      <ApplicationForm
        mode="rich"
        practitionerType={inv.practitionerType}
        discipline={inv.discipline}
        role={role}
        subCategoryOptions={role ? SUBCATEGORY_OPTIONS[role] : undefined}
        invitation={inv}
      />
    </ApplyShell>
  )
}

export default function PublicApplyApp() {
  return (
    <Routes>
      <Route path="" element={<ApplyLanding />} />
      <Route path="mhp" element={<ApplyMhpRolePicker />} />
      <Route path="mhp/:role" element={<ApplyMhpRolePage />} />
      <Route path="i/:token" element={<ApplyInvitationPage />} />
      {/* Phase 14w — applicant resumes a submitted application via email-OTP. Lands here
          when they revisit /apply/i/<token> after the invitation has been consumed, or via
          a saved /apply/resume URL they bookmarked. */}
      <Route path="resume" element={<ApplyResumePage />} />
      <Route path=":type" element={<ApplyTypePage />} />
    </Routes>
  )
}

/**
 * Phase 14w / 14y — resume an in-flight application. The applicant picks the channel they
 * want to verify with (email or mobile/WhatsApp), receives an OTP, verifies it, and we
 * mint a fresh JWT so they land back on the PostLoginEnrichmentForm with their progress
 * hydrated. Mobile is essential for applicants whose only contact on the original
 * invitation was a WhatsApp number.
 */
function ApplyResumePage() {
  const [params] = useSearchParams()
  const initialEmail = params.get('email') || ''
  const initialMobile = params.get('mobile') || ''
  const source = params.get('source') || ''
  const hintedChannel = params.get('channel') === 'mobile' ? 'mobile' : params.get('channel') === 'email' ? 'email' : ''
  const maskedHint = params.get('hint') || ''
  const isInvitationResume = source === 'invitation'
  const [channel, setChannel] = useState(
    hintedChannel || (initialMobile && !initialEmail ? 'mobile' : 'email'),
  )
  const [email, setEmail] = useState(initialEmail)
  const [mobile, setMobile] = useState(initialMobile)
  const [otpSent, setOtpSent] = useState(false)
  const [otpSessionId, setOtpSessionId] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [otpDisabled, setOtpDisabled] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [resumeData, setResumeData] = useState(null)

  const isEmail = channel === 'email'
  const contactValue = isEmail ? email : mobile
  const channelLabel = isEmail ? 'email address' : 'WhatsApp number'

  // Pull the disable-otp flag so dev / staging can advance without OTP.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await applyAnonAxios.get(`${PUBLIC_BASE}/config`)
        if (!cancelled) {
          const data = res?.data?.data || res?.data
          setOtpDisabled(Boolean(data?.otpDisabled))
        }
      } catch {
        /* non-fatal — assume OTP required */
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  function resetOtpState() {
    setOtpSent(false)
    setOtpCode('')
    setOtpSessionId('')
    setError('')
  }

  async function handleSendOtp() {
    if (!contactValue.trim()) {
      setError(`Enter your ${isEmail ? 'email' : 'mobile'} first.`)
      return
    }
    setError('')
    setSubmitting(true)
    try {
      const recaptchaToken = await fetchRecaptchaToken('apply_otp_send')
      const res = await applyAnonAxios.post(`${PUBLIC_BASE}/otp/send`, {
        channel: isEmail ? 'email' : 'mobile',
        value: contactValue.trim(),
        recaptchaToken,
      })
      const data = res?.data?.data || res?.data
      if (data?.disabled) {
        // OTP gating off — proceed straight to resume verify with no session id.
        await handleVerifyAndResume({ skipOtp: true })
      } else if (data?.sessionId) {
        setOtpSessionId(data.sessionId)
        setOtpSent(true)
        toast.success(
          isEmail
            ? 'Check your inbox for the sign-in code.'
            : 'Check WhatsApp for the sign-in code.',
        )
      } else {
        setError('Could not send code. Try again in a moment.')
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not send code.')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleVerifyAndResume({ skipOtp } = {}) {
    setError('')
    setSubmitting(true)
    try {
      // First verify the OTP unless we're in disable-otp bypass mode.
      if (!skipOtp) {
        const recaptchaVerify = await fetchRecaptchaToken('apply_otp_verify')
        const verifyRes = await applyAnonAxios.post(`${PUBLIC_BASE}/otp/verify`, {
          sessionId: otpSessionId,
          otp: otpCode.trim(),
          recaptchaToken: recaptchaVerify,
        })
        const verifyData = verifyRes?.data?.data || verifyRes?.data
        if (!verifyData?.verified) {
          setError("That code didn't match. Try again.")
          setSubmitting(false)
          return
        }
      }
      // Now mint a JWT against the verified session. The resume endpoint accepts EITHER
      // (email + emailSession) or (mobile + mobileSession); we send only the pair the
      // applicant verified.
      const recaptchaResume = await fetchRecaptchaToken('apply_resume')
      const resumeBody = {
        recaptchaToken: recaptchaResume,
      }
      if (isEmail) {
        resumeBody.email = email.trim()
        resumeBody.emailVerificationSessionId = skipOtp ? null : otpSessionId
      } else {
        resumeBody.mobile = mobile.trim()
        resumeBody.mobileVerificationSessionId = skipOtp ? null : otpSessionId
      }
      const resumeRes = await applyAnonAxios.post(`${PUBLIC_BASE}/resume`, resumeBody)
      const data = resumeRes?.data?.data || resumeRes?.data
      if (!data?.authToken) {
        setError(
          data?.message ||
            "Could not sign you in. If you applied recently and haven't received a link, contact namaste@aumhum.com.",
        )
        setSubmitting(false)
        return
      }
      setResumeData(data)
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Could not sign you in. If you applied recently and haven't received a link, contact namaste@aumhum.com.",
      )
    } finally {
      setSubmitting(false)
    }
  }

  if (resumeData) {
    return (
      <PostLoginEnrichmentForm
        authToken={resumeData.authToken}
        summary={{
          practitionerId: resumeData.practitionerId,
          practitionerSlug: resumeData.practitionerSlug,
          practitionerType: resumeData.practitionerType,
        }}
        practitionerType={resumeData.practitionerType}
        // No discipline / role context on resume — the catalog stays at the discipline that
        // was set during Stage 1 (which we don't echo back from /resume); the form falls
        // back to free-text qualifications when the catalog isn't loaded, which is the right
        // graceful degradation.
        discipline={null}
        role={null}
        qualificationCatalog={null}
        specializationCatalog={null}
        pendingLinkedin=""
      />
    )
  }

  return (
    <ApplyShell
      title="Resume your application"
      subtitle={
        isInvitationResume
          ? 'Your invitation has already created an application. Sign in now to complete the remaining required steps.'
          : "Sign back in with the email or mobile you applied with - whichever the support team contacted you on. We'll send a sign-in code to that channel."
      }
    >
      <ApplyMeta pageKind="resume" />
      <Stack spacing={2}>
        {isInvitationResume && (
          <Alert severity="info" icon={<VerifiedUser />} sx={{ borderRadius: 3 }}>
            {maskedHint ? (
              <>
                Continue with the {channelLabel} on the account: <strong>{maskedHint}</strong>.
                Enter the full {channelLabel} below to receive your one-time sign-in code.
              </>
            ) : (
              <>
                Continue with the contact used for this application. Signing in is required to
                finish your profile.
              </>
            )}
          </Alert>
        )}
        {/* Phase 14y — channel picker. Some applicants only get the invite over WhatsApp,
            so mobile must be a first-class option. */}
        <Box
          sx={{
            display: 'inline-flex',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 999,
            p: 0.5,
            alignSelf: 'flex-start',
          }}
        >
          <Button
            size="small"
            variant={isEmail ? 'contained' : 'text'}
            onClick={() => {
              setChannel('email')
              resetOtpState()
            }}
            sx={{ borderRadius: 999, minWidth: 100 }}
            disabled={submitting || (isInvitationResume && hintedChannel && hintedChannel !== 'email')}
          >
            Email
          </Button>
          <Button
            size="small"
            variant={!isEmail ? 'contained' : 'text'}
            onClick={() => {
              setChannel('mobile')
              resetOtpState()
            }}
            sx={{ borderRadius: 999, minWidth: 130 }}
            disabled={submitting || (isInvitationResume && hintedChannel && hintedChannel !== 'mobile')}
          >
            Mobile / WhatsApp
          </Button>
        </Box>

        {isEmail ? (
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            disabled={otpSent}
            helperText={
              isInvitationResume && maskedHint && isEmail
                ? `Use the full email matching ${maskedHint}.`
                : 'The email address you applied with.'
            }
          />
        ) : (
          <TextField
            label="Mobile (with country code)"
            value={mobile}
            onChange={(e) => setMobile(e.target.value)}
            fullWidth
            disabled={otpSent}
            placeholder="+919876543210"
            helperText={
              isInvitationResume && maskedHint && !isEmail
                ? `Use the full WhatsApp number ending in ${maskedHint.slice(-4)}.`
                : 'Include the country code, e.g. +91 for India. Code arrives on WhatsApp.'
            }
          />
        )}
        {!otpSent ? (
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={handleSendOtp}
              disabled={submitting || !contactValue.trim()}
            >
              {submitting ? 'Sending…' : otpDisabled ? 'Continue' : 'Send sign-in code'}
            </Button>
          </Stack>
        ) : (
          <>
            <TextField
              label="Sign-in code"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value)}
              fullWidth
              autoFocus
              helperText={
                isEmail
                  ? 'The 6-digit code we just emailed you.'
                  : 'The 6-digit code we just sent on WhatsApp.'
              }
            />
            <Stack direction="row" spacing={1} justifyContent="space-between">
              <Button
                size="small"
                onClick={resetOtpState}
              >
                Use a different {isEmail ? 'email' : 'mobile'}
              </Button>
              <Button
                variant="contained"
                onClick={() => handleVerifyAndResume()}
                disabled={submitting || !otpCode.trim()}
              >
                {submitting ? 'Signing you in…' : 'Continue'}
              </Button>
            </Stack>
          </>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {!isInvitationResume && (
          <Alert severity="info" icon={false} sx={{ mt: 1 }}>
            New to AUMHUM? <a href="/apply">Start a fresh application →</a>
          </Alert>
        )}
      </Stack>
    </ApplyShell>
  )
}
